import { z } from 'zod';
import type { SafeParseResult } from './zod-utils';

export interface HttpRequestHeaders {
    readonly [headerName: string]: string;
}

/*
 * This uses the global Fetch `RequestInit` for cross-runtime compatibility. In browsers it comes from `lib.dom`;
 * in Node it comes from `@types/node` (backed by undici) as a type-only dependency. We narrow only `headers`
 * to a string map to keep the API stable and serializable across runtimes.
 */
export interface FetchRequestInit extends Omit<RequestInit, 'headers'> {
    readonly headers?: HttpRequestHeaders;
}

export type FetchImplementation = (
    input: string | URL,
    init?: FetchRequestInit
) => Promise<Response>;

type FetchArguments = [input: string | URL, init?: FetchRequestInit];

export interface PillarApiClientOptions {
    readonly onSessionExpired?: () => void;
    readonly baseUrl?: string;
    readonly getRequestHeaders?: () => HttpRequestHeaders;
    readonly onResponse?: (response: Response) => void;
    readonly onTraceContext?: (traceHeaderValue: string) => void;
    readonly onValidationError?: (error: ApiValidationError) => void;
}

function isFormDataBody(value: unknown): value is FormData {
    return value instanceof FormData;
}

/**
 * API Error class for HTTP errors
 */
export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly statusText: string,
        message: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export interface ApiResponseValidationError {
    readonly kind: 'response-body';
    readonly endpoint: string;
    readonly issues: z.core.$ZodIssue[];
    readonly response: unknown;
}

export interface ApiTraceHeaderValidationError {
    readonly kind: 'trace-header';
    readonly headerValue: string;
    readonly issues: z.core.$ZodIssue[];
}

export type ApiValidationError = ApiResponseValidationError | ApiTraceHeaderValidationError;

// Import all types and schemas needed for API functions
import type {
    ActiveRoleAssignmentResponse,
    CompleteCustomerPaymentLinkAttachmentRequest,
    LoginRequest,
    SessionUserProfileDetailedResponse,
    VerifyTotpRequest,
    VerifyEmailRequest,
    VerifyEmailResponse,
    CreateCustomerSelfOnboardingRequest,
    CreateCustomerSelfOnboardingResponse,
    RequestPasswordResetRequest,
    RequestPasswordResetResponse,
    ValidatePasswordResetTokenRequest,
    ValidatePasswordResetTokenResponse,
    ResetPasswordRequest,
    ResetPasswordResponse,
    ChangePasswordRequest,
    SetupTotpResponse,
    EnableTotpRequest,
    GenerateBackupCodesResponse,
    SetDefaultRoleAssignmentRequest,
    SetDefaultRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentUuidRequest,
    StartImpersonationRequest,
    ImpersonationContextResponse,
    SendVerificationEmailResponse,
    SingleUseTokensSearchRequest,
    ListSingleUseTokensResponse,
} from './auth.types';
import type {
    CreateOrganizationalUserRequest,
    CreateOrganizationalUserResponse,
    CreateInternalUserResponse,
    CreateUserRequest,
    UpdateUserRequest,
    UpdateUserResponse,
    UserProfileDetailedPublicResponse,
    DeleteMyAccountRequest,
    DeleteMyAccountResponse,
    AssignRoleRequest,
    RevokeRoleRequest,
    UpdateRoleAssignmentRequest,
    ResetPasswordAdminRequest,
    RequestAddEmailVerificationRequest,
    RequestAddEmailVerificationResponse,
    SetUserEmailsRequest,
} from './user.types';
import type {
    ApplyOrganizationUsersCsvImportRequest,
    ApplyOrganizationUsersCsvImportResponse,
    CreateOrganizationDocumentTemplateRequest,
    CreateIsoRequest,
    CreateIsoResponse as OrganizationCreateIsoResponse,
    CreateMerchantRequest,
    CreateMerchantResponse as OrganizationCreateMerchantResponse,
    AssignMerchantToIsoRequest,
    SetOrganizationParentRequest,
    UpdateOrganizationRequest,
    GetOrganizationResponse,
    AssignMerchantToIsoResponse,
    SetOrganizationParentResponse,
    UpdateOrganizationResponse,
    ExportMerchantBackupResponse,
    GetMerchantChecklistResponse,
    GetMerchantChecklistCheckResponse,
    ListOrganizationDocumentTemplatesResponse,
    MerchantChecklistCheckCode,
    OrganizationDocumentTemplateOverrideDescriptor,
    RestoreMerchantBackupDryRunRequest,
    RestoreMerchantBackupDryRunResponse,
    RestoreMerchantBackupApplyRequest,
    RestoreMerchantBackupApplyResponse,
    PreviewOrganizationUsersCsvImportRequest,
    PreviewOrganizationUsersCsvImportResponse,
    SetOrganizationDocumentTemplateActiveRequest,
    UpdateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateResponse,
} from './organization.types';
import type {
    OrganizationOmnisearchSearchRequest,
    OrganizationOmnisearchPageResponse,
    UserOmnisearchPageRequest,
    UserOmnisearchPageResponse,
    ListSubsidiaryIsosPageResponse,
    ListSubsidiaryMerchantsPageResponse,
    ListDirectOrganizationUsersPageResponse,
} from './omnisearch.types';
import type {
    PublicUserListingSearchRequest,
    PublicUserListingPageResponse,
} from './user-listing.types';
import type {
    AcceptLegalDocumentRequest,
    CreateLegalDocumentRequest,
    CreateLegalDocumentResponse,
    GetLegalDocumentResponse,
    LegalDocumentsSearchRequest,
    ListPendingRequiredLegalDocumentsResponse,
    SearchLegalDocumentsPageResponse,
} from './legal-documents.types';
import type {
    ListTransactionLogPageResponse,
    TransactionLogSearchRequest,
} from './transaction-log.types';
import type {
    CustomerUsersSearchRequest,
    CustomerUsersPageResponse,
    MerchantCustomerRecordDetailsResponse,
    UpdateMerchantCustomerRecordRequest,
    MerchantCustomerRecordSearchRequest,
    MerchantCustomerRecordSearchPageResponse,
} from './customer.types';
import type {
    ExpireSessionUuidRequest,
    ExpireUserSessionRequest,
    GetSessionsResponse,
    SessionsSearchRequest,
    ListSessionsPageResponse,
    UpdateSessionConsoleRequest,
} from './session.types';
import type {
    ConfigResponse,
    UpdateConfigRequest,
    CombinedConfigResponse,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest,
} from './samba-config.types';
import type {
    AwsCredentialsResponse,
    GetCallerIdentityResponse,
    GetSystemInfoResponse,
    GetSesDiagnosticsResponse,
} from './diagnostics.types';
import type {
    ListSecretsResponse,
    GetSecretResponse,
    SecretMetadataResponse,
    SecretWithCredentialsResponse,
    CreateSecretRequest,
    CreateSecretResponse,
    UpdateSecretRequest,
    CredentialUsagesResponse,
    DisconnectCredentialUsagesRequest,
    DisconnectCredentialUsagesResponse,
    SecretIdentityReference,
    SecretOrganizationUuidReference,
    SecretOrganizationReference,
    SecretUuidReference,
} from './secret.types';
import {
    SecretOrganizationReferenceSchema,
    SecretOrganizationUuidReferenceSchema,
    SecretUuidReferenceSchema,
} from './schemas/secret.schemas';
import type {
    ListPublicBundlesResponse,
    OrganizationPermissionAuditResponse,
    OrganizationScopedPermissionSourceOptionsResponse,
    PermissionsBundlePublic,
    PolicyPermissionBundleRoleAssignmentsSearchPageResponse,
    PolicyPermissionBundleRoleAssignmentsSearchRequest,
    PermissionSourceRoleAssignmentsSearchPageResponse,
    PermissionSourceRoleAssignmentsSearchRequest,
    RoleAssignmentPermissionsView,
    RoleAssignmentPermissionAuditResponse,
    GrantPermissionRequest,
    RevokePermissionRequest,
    RepairAuditedPermissionRequest,
    AssignBundleRequest,
    CreateSharedBundleRequest,
    UpdateBundleRequest,
    RoleEligibility,
    PolicyPermissionBundlesSearchRequest,
    PolicyPermissionBundlesSearchPageResponse,
    CreatePolicyPermissionBundleRequest,
    UpdatePolicyPermissionBundleRequest,
    ListPolicyPermissionGrantsResponse,
    GrantPolicyPermissionBundleRequest,
} from './permission.types';
import {
    OrganizationPermissionAuditResponseSchema,
    RoleAssignmentPermissionAuditResponseSchema,
} from './permission.types';
import type {
    MessageResponse,
    SambaVersionResponse,
    SambaLatestReleaseQuery,
    SambaLatestReleaseResponse,
} from './common.types';
import type {
    GetVaultTransactionsResponse,
    GetTransactionDetailsResponse,
    GatewayTransactionDetailsFetchResult,
    GetReceiptDataResponse,
    GetPaymentAuditLogResponse,
    GetSavedCardTokensRequest,
    GetSavedCardTokensResponse,
    GetDelegateTokenRequest,
    GetDelegateTokenResponse,
    BinLookupRequest,
    BinLookupResponse,
    GetPaymentConfigurationRequest,
    PaymentConfigurationResponse,
    ListCardPaymentTerminalDevicesRequest,
    ListAccessibleTerminalsInfoRequest,
    ListAccessibleCredentialsInfoRequest,
    ListCardPaymentTerminalDevicesResponse,
    ListAccessibleCredentialsInfoResponse,
    ListAccessibleTerminalsInfoResponse,
    GetReconcilePaymentsCandidatesRequest,
    GetReconcilePaymentsCandidatesResponse,
    RefundTransactionRequest,
    RefundTransactionResponse,
    VoidTransactionRequest,
    VoidTransactionResponse,
    TerminalSaleRequest,
    TerminalSaleResponse,
    TerminalAbortRequest,
    TerminalAbortResponse,
    RecordTokenRequest,
    RecordTokenResponse,
    ProcessCardTokenPaymentRequest,
    ProcessCardTokenPaymentResponse,
    ProcessAchPaymentRequest,
    ProcessAchPaymentResponse,
    RecordReconciliationRequest,
    RecordReconciliationResponse,
    RecordPaymentRequestReconciliationRequest,
    RecordPaymentRequestReconciliationResponse,
    CreateDraftPaymentRequestRequest,
    CreateDraftPaymentRequestResponse,
    PaymentRequestOwnerCandidatesResponse,
    UploadPaymentRequestDocumentResponse,
    FinalizeSendPaymentRequestRequest,
    FinalizeSendPaymentRequestResponse,
    MerchantPaymentRequestSearchRequest,
    MerchantPaymentRequestListResponse,
    CustomerPaymentMethodListRequest,
    PaymentRequestDeliveryEventList,
    PaymentRequestDetailsView,
    CustomerPaymentMethodListResponse,
    CustomerPaymentRequestDetailsRequest,
    CustomerPaymentMethodBootstrapRequest,
    CustomerPaymentMethodBootstrapResponse,
    CustomerPaymentMethodBinLookupRequest,
    CreateCustomerPaymentMethodRequest,
    CreateCustomerPaymentMethodResponse,
    CustomerPortalDashboardRequest,
    CustomerPortalDashboardResponse,
    CustomerPaymentHistoryRequest,
    CustomerPaymentHistoryResponse,
    CustomerPaymentRequestSearchRequest,
    CustomerPaymentRequestListResponse,
    GetPaymentAuditLogSearchRequest,
    GetVaultTransactionsSearchRequest,
    ResendPaymentRequestRequest,
    UpdateCustomerPaymentMethodRequest,
    UpdateCustomerPaymentMethodResponse,
    CancelAndReissuePaymentRequestRequest,
    CancelAndReissuePaymentRequestResponse,
    PublicPaymentLinkActionResponse,
    PublicPaymentRequestView,
    GuestPaymentEntryContextView,
    AuthenticatedPaymentEntryContextView,
    GuestPaymentSuccessView,
    GuestBinLookupRequest,
    GuestProcessCardTokenPaymentRequest,
    AuthenticatedProcessCardTokenPaymentRequest,
    GuestProcessStoredPaymentMethodPaymentRequest,
} from './payment.types';
import type {
    ProcessorSearchRequest,
    ProcessorSearchPageResponse,
    CreateProcessorRequest,
    GetProcessorResponse,
    UpdateProcessorRequest,
    MerchantAccountSearchRequest,
    MerchantAccountSearchPageResponse,
    CreateMerchantAccountRequest,
    UpdateMerchantAccountRequest,
    GetMerchantAccountResponse,
    LogicalTerminalSearchRequest,
    LogicalTerminalSearchPageResponse,
    CreateLogicalTerminalRequest,
    UpdateLogicalTerminalRequest,
    GetLogicalTerminalResponse,
    MerchantOrganizationSearchRequest,
    MerchantOrganizationSearchPageResponse,
    PreviewLogicalTerminalOrganizationChangeRequest,
    PreviewLogicalTerminalOrganizationChangeResponse,
    TerminalDeviceSearchRequest,
    TerminalDeviceSearchPageResponse,
    CreateTerminalDeviceRequest,
    UpdateTerminalDeviceRequest,
    GetTerminalDeviceResponse,
} from './payments_infrastructure.types';
import {
    CompleteCustomerPaymentLinkAttachmentRequestSchema,
    LoginResponseSchema,
    SessionUserProfileDetailedResponseSchema,
    ActiveRoleAssignmentResponseSchema,
    VerifyTotpResponseSchema,
    VerifyEmailResponseSchema,
    CreateCustomerSelfOnboardingResponseSchema,
    RequestPasswordResetResponseSchema,
    ValidatePasswordResetTokenResponseSchema,
    ResetPasswordResponseSchema,
    SendVerificationEmailResponseSchema,
    SetupTotpResponseSchema,
    GenerateBackupCodesResponseSchema,
    StartImpersonationRequestSchema,
    ImpersonationContextResponseSchema,
    SingleUseTokensSearchRequestSchema,
    ListSingleUseTokensResponseSchema,
} from './schemas/auth.schemas';
import {
    CreateOrganizationalUserResponseSchema,
    CreateInternalUserResponseSchema,
    UserProfileDetailedPublicResponseSchema,
    DeleteMyAccountResponseSchema,
    UpdateUserResponseSchema,
    RequestAddEmailVerificationRequestSchema,
    RequestAddEmailVerificationResponseSchema,
} from './schemas/user.schemas';
import {
    ExpireSessionUuidRequestSchema,
    ExpireUserSessionRequestSchema,
    GetSessionsResponseSchema,
    SessionsSearchRequestSchema,
    ListSessionsPageResponseSchema,
    UpdateSessionConsoleRequestSchema,
} from './schemas/session.schemas';
import {
    ApplyOrganizationUsersCsvImportRequestSchema,
    ApplyOrganizationUsersCsvImportResponseSchema,
    CreateOrganizationDocumentTemplateRequestSchema,
    GetOrganizationResponseSchema,
    AssignMerchantToIsoResponseSchema,
    OrganizationDocumentTemplateOverrideDescriptorSchema,
    SetOrganizationParentResponseSchema,
    UpdateOrganizationResponseSchema,
    CreateIsoResponseSchema,
    CreateMerchantResponseSchema,
    ExportMerchantBackupResponseSchema,
    GetMerchantChecklistResponseSchema,
    GetMerchantChecklistCheckResponseSchema,
    ListOrganizationDocumentTemplatesResponseSchema,
    RestoreMerchantBackupDryRunRequestSchema,
    RestoreMerchantBackupDryRunResponseSchema,
    RestoreMerchantBackupApplyRequestSchema,
    RestoreMerchantBackupApplyResponseSchema,
    PreviewOrganizationUsersCsvImportRequestSchema,
    PreviewOrganizationUsersCsvImportResponseSchema,
    SetOrganizationDocumentTemplateActiveRequestSchema,
    UpdateOrganizationDocumentTemplateRequestSchema,
    ValidateOrganizationDocumentTemplateRequestSchema,
    ValidateOrganizationDocumentTemplateResponseSchema,
} from './schemas/organization.schemas';
import {
    OrganizationOmnisearchSearchRequestSchema,
    OrganizationOmnisearchPageResponseSchema,
    UserOmnisearchPageResponseSchema,
    ListSubsidiaryIsosPageResponseSchema,
    ListSubsidiaryMerchantsPageResponseSchema,
    ListDirectOrganizationUsersPageResponseSchema,
} from './schemas/omnisearch.schemas';
import {
    PublicUserListingPageResponseSchema,
    PublicUserListingSearchRequestSchema,
} from './schemas/user-listing.schemas';
import {
    AcceptLegalDocumentRequestSchema,
    CreateLegalDocumentRequestSchema,
    CreateLegalDocumentResponseSchema,
    GetLegalDocumentResponseSchema,
    LegalDocumentUuidParamSchema,
    LegalDocumentsSearchRequestSchema,
    ListPendingRequiredLegalDocumentsResponseSchema,
    SearchLegalDocumentsPageResponseSchema,
} from './schemas/legal-documents.schemas';
import {
    ListTransactionLogPageResponseSchema,
    TransactionLogSearchRequestSchema,
} from './schemas/transaction-log.schemas';
import {
    CustomerUsersSearchRequestSchema,
    CustomerUsersPageResponseSchema,
    MerchantCustomerRecordDetailsResponseSchema,
    MerchantCustomerRecordSearchRequestSchema,
    MerchantCustomerRecordSearchPageResponseSchema,
    UpdateMerchantCustomerRecordRequestSchema,
} from './schemas/customer.schemas';
import {
    ConfigResponseSchema,
    CombinedConfigResponseSchema,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema,
} from './schemas/samba-config.schemas';
import {
    GetVaultTransactionsResponseSchema,
    GetVaultTransactionsSearchRequestSchema,
    GetTransactionDetailsResponseSchema,
    GatewayTransactionDetailsFetchResultSchema,
    GetReceiptDataResponseSchema,
    GetPaymentAuditLogResponseSchema,
    GetPaymentAuditLogSearchRequestSchema,
    GetSavedCardTokensResponseSchema,
    GetDelegateTokenResponseSchema,
    BinLookupResponseSchema,
    GetPaymentConfigurationRequestSchema,
    PaymentConfigurationResponseSchema,
    ListCardPaymentTerminalDevicesRequestSchema,
    ListAccessibleTerminalsInfoRequestSchema,
    ListAccessibleCredentialsInfoRequestSchema,
    ListCardPaymentTerminalDevicesResponseSchema,
    ListAccessibleCredentialsInfoResponseSchema,
    ListAccessibleTerminalsInfoResponseSchema,
    GetReconcilePaymentsCandidatesResponseSchema,
    RefundTransactionResponseSchema,
    VoidTransactionResponseSchema,
    TerminalSaleResponseSchema,
    TerminalAbortResponseSchema,
    RecordTokenResponseSchema,
    ProcessCardTokenPaymentResponseSchema,
    ProcessAchPaymentResponseSchema,
    RecordReconciliationResponseSchema,
    RecordPaymentRequestReconciliationRequestSchema,
    RecordPaymentRequestReconciliationResponseSchema,
    CreateDraftPaymentRequestRequestSchema,
    CreateDraftPaymentRequestResponseSchema,
    PaymentRequestOwnerCandidatesResponseSchema,
    UploadPaymentRequestDocumentResponseSchema,
    FinalizeSendPaymentRequestRequestSchema,
    FinalizeSendPaymentRequestResponseSchema,
    MerchantPaymentRequestSearchRequestSchema,
    MerchantPaymentRequestListResponseSchema,
    CustomerPaymentMethodListRequestSchema,
    PaymentRequestDeliveryEventListSchema,
    PaymentRequestDetailsViewSchema,
    CustomerPaymentMethodListResponseSchema,
    CustomerPaymentRequestDetailsRequestSchema,
    CustomerPaymentMethodBootstrapRequestSchema,
    CustomerPaymentMethodBootstrapResponseSchema,
    CustomerPaymentMethodBinLookupRequestSchema,
    CreateCustomerPaymentMethodRequestSchema,
    CreateCustomerPaymentMethodResponseSchema,
    CustomerPortalDashboardRequestSchema,
    CustomerPortalDashboardResponseSchema,
    CustomerPaymentHistoryRequestSchema,
    CustomerPaymentHistoryResponseSchema,
    CustomerPaymentRequestSearchRequestSchema,
    CustomerPaymentRequestListResponseSchema,
    ResendPaymentRequestRequestSchema,
    UpdateCustomerPaymentMethodRequestSchema,
    UpdateCustomerPaymentMethodResponseSchema,
    CancelAndReissuePaymentRequestRequestSchema,
    CancelAndReissuePaymentRequestResponseSchema,
    PublicPaymentLinkActionResponseSchema,
    PublicPaymentRequestViewSchema,
    GuestPaymentEntryContextViewSchema,
    AuthenticatedPaymentEntryContextViewSchema,
    GuestPaymentSuccessViewSchema,
    GuestBinLookupRequestSchema,
    GuestProcessCardTokenPaymentRequestSchema,
    AuthenticatedProcessCardTokenPaymentRequestSchema,
    GuestProcessStoredPaymentMethodPaymentRequestSchema,
} from './schemas/payment.schemas';
import {
    ProcessorSearchRequestSchema,
    ProcessorSearchPageResponseSchema,
    CreateProcessorRequestSchema,
    GetProcessorResponseSchema,
    UpdateProcessorRequestSchema,
    MerchantAccountSearchRequestSchema,
    MerchantAccountSearchPageResponseSchema,
    MerchantAccountUuidParamSchema,
    CreateMerchantAccountRequestSchema,
    UpdateMerchantAccountRequestSchema,
    GetMerchantAccountResponseSchema,
    LogicalTerminalSearchRequestSchema,
    LogicalTerminalSearchPageResponseSchema,
    LogicalTerminalUuidParamSchema,
    CreateLogicalTerminalRequestSchema,
    UpdateLogicalTerminalRequestSchema,
    GetLogicalTerminalResponseSchema,
    MerchantOrganizationSearchRequestSchema,
    MerchantOrganizationSearchPageResponseSchema,
    PreviewLogicalTerminalOrganizationChangeRequestSchema,
    PreviewLogicalTerminalOrganizationChangeResponseSchema,
    TerminalDeviceSearchRequestSchema,
    TerminalDeviceSearchPageResponseSchema,
    TerminalDeviceUuidParamSchema,
    CreateTerminalDeviceRequestSchema,
    UpdateTerminalDeviceRequestSchema,
    GetTerminalDeviceResponseSchema,
} from './schemas/payments-infrastructure.schemas';
import {
    SambaLatestReleaseResponseSchema,
    SambaVersionResponseSchema,
} from './schemas/common.schemas';
import {
    EnvironmentVariablesResponseSchema,
    AwsCredentialsResponseSchema,
    GetCallerIdentityResponseSchema,
    GetSystemInfoResponseSchema,
    GetSesDiagnosticsResponseSchema,
} from './schemas/diagnostics.schemas';
import {
    ListSecretsResponseSchema,
    GetSecretResponseSchema,
    SecretMetadataResponseSchema,
    SecretWithCredentialsResponseSchema,
    CreateSecretRequestSchema,
    CreateSecretResponseSchema,
    ListSecretsForOrganizationQuerySchema,
    CredentialUsagesResponseSchema,
    DisconnectCredentialUsagesRequestSchema,
    DisconnectCredentialUsagesResponseSchema,
} from './schemas/secret.schemas';
import type { ListSecretsForOrganizationQuery } from './secret.types';
import {
    AssignBundleRequestSchema,
    GrantPermissionRequestSchema,
    ListPublicBundlesResponseSchema,
    PermissionsBundlePublicSchema,
    RepairAuditedPermissionRequestSchema,
    SharedPermissionBundlesSearchPageResponseSchema,
    SharedPermissionBundlesSearchRequestSchema,
    RevokePermissionRequestSchema,
    RoleEligibilitySchema,
    RoleAssignmentPermissionsViewSchema,
    PermissionSourceRoleAssignmentsSearchRequestSchema,
    PermissionSourceRoleAssignmentsSearchPageResponseSchema,
    PolicyPermissionBundleRoleAssignmentsSearchRequestSchema,
    PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchema,
    OrganizationScopedPermissionSourceOptionsResponseSchema,
    PolicyPermissionBundlesSearchRequestSchema,
    PolicyPermissionBundlesSearchPageResponseSchema,
    ListPolicyPermissionGrantsResponseSchema,
    GrantPolicyPermissionBundleRequestSchema,
} from './schemas/permission.schemas';
import type {
    SharedPermissionBundlesSearchPageResponse,
    SharedPermissionBundlesSearchRequest,
} from './permission.types';
import {
    CanCreateUserResponseSchema,
    CanModifyUserResponseSchema,
    CanDeleteUserResponseSchema,
    HasAccessToOrganizationResponseSchema,
    AccessibleOrganizationIdsResponseSchema,
    AccessibleUserIdsResponseSchema,
    AccessibleOrganizationsResponseSchema,
    AccessibleUsersResponseSchema,
    IsAuthorizedRequestSchema,
    IsAuthorizedResponseSchema,
    type IsAuthorizedRequest,
    type AccessibleOrganizationIdsRequest,
    type AccessibleUserIdsRequestNoPkey,
    HasAccessToUserResponseSchema,
    CanImpersonateRoleAssignmentResponseSchema,
} from './schemas/authorization.schemas';
import {
    MessageResponseSchema,
    NestJsExceptionResponseSchema,
    VoidResponseSchema,
} from './schemas/common.schemas';

/**
 * Check if Content-Type header indicates JSON
 */
function isJsonContentType(contentType: string | null): boolean {
    if (contentType === null) {
        return false;
    }
    const normalized = contentType.toLowerCase().trim();
    return normalized.startsWith('application/json') || normalized.startsWith('application/json;');
}

function normalizeWhitespace(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
}

function stripHtml(value: string): string {
    const withoutComments: string = value.replace(/<!--[\s\S]*?-->/g, ' ');
    const withoutTags: string = withoutComments.replace(/<[^>]+>/g, ' ');
    return normalizeWhitespace(withoutTags);
}

function containsHtml(value: string): boolean {
    return /<!doctype\s+html|<html[\s>]|<\/?[a-z][\s\S]*?>/i.test(value);
}

function getFriendlyServerErrorMessage(status: number, statusText: string): string {
    if (status === 502) {
        return 'Temporary upstream network error. Please retry.';
    }
    if (status === 503) {
        return 'Service temporarily unavailable. Please retry.';
    }
    if (status === 504) {
        return 'Gateway timeout while contacting the server. Please retry.';
    }
    return `Server error (HTTP ${status}: ${statusText}). Please retry.`;
}

/**
 * Extract error message from HTTP error response body
 * Checks Content-Type header to determine if body is JSON
 * Attempts to parse NestJS exception response format if JSON
 * Falls back to raw body string if not JSON
 */
function extractErrorMessage(
    responseBody: string,
    response: Response,
    status: number,
    statusText: string
): string {
    if (!responseBody || responseBody.trim() === '') {
        return `HTTP ${status}: ${statusText}`;
    }

    const contentType: string | null = response.headers.get('content-type');
    if (!isJsonContentType(contentType)) {
        if (status >= 500 && status < 600) {
            const plainTextResponseBody: string = stripHtml(responseBody);
            if (containsHtml(responseBody) || plainTextResponseBody.length === 0) {
                return getFriendlyServerErrorMessage(status, statusText);
            }
        }
        return responseBody;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(responseBody);
    } catch {
        // If Content-Type says JSON but parsing fails, return raw body
        return responseBody;
    }

    const result = NestJsExceptionResponseSchema.safeParse(parsed);
    if (result.success) {
        const validationErrors = result.data.errors;
        if (validationErrors !== undefined && validationErrors.length > 0) {
            const validationErrorSummaries: ReadonlyArray<string> = validationErrors.map(
                (validationError): string => {
                    const validationPath: string =
                        validationError.path !== undefined ? validationError.path : '(unknown path)';
                    const validationMessage: string =
                        validationError.message !== undefined
                            ? validationError.message
                            : 'validation error';
                    return `${validationPath}: ${validationMessage}`;
                }
            );
            const baseMessageParseResult = z.string().safeParse(result.data.message);
            const baseMessage: string = baseMessageParseResult.success
                ? baseMessageParseResult.data
                : 'Validation failed';
            return `${baseMessage} (${validationErrorSummaries.join('; ')})`;
        }
        const messageParseResult = z.string().safeParse(result.data.message);
        if (messageParseResult.success) {
            return messageParseResult.data;
        }
        const messageListParseResult = z.array(z.string()).safeParse(result.data.message);
        if (messageListParseResult.success) {
            return messageListParseResult.data.join(', ');
        }
    }

    return JSON.stringify(parsed);
}

/**
 * Factory function to create PILLAR API client instances
 * @param onSessionExpired Optional callback for handling 401 responses
 * @param baseUrl Base URL for the API (defaults to '/api')
 * @param onTraceContext Optional callback for propagating trace context
 * @param onValidationError Optional callback for response validation failures
 * @returns Object containing all API client functions
 */
export function createPillarApi(
    onSessionExpired?: () => void,
    baseUrl: string = '/api',
    onTraceContext?: (traceHeaderValue: string) => void,
    onValidationError?: (error: ApiValidationError) => void
) {
    const options: PillarApiClientOptions = {
        onSessionExpired,
        baseUrl,
        onTraceContext,
        onValidationError,
    };
    return createPillarApiWithOptions(options);
}

export function dedupe<ArgumentsTuple extends readonly unknown[], ReturnValue>(
    functionToDedupe: (...args: ArgumentsTuple) => Promise<ReturnValue>
): (...args: ArgumentsTuple) => Promise<ReturnValue> {
    const cacheBySerializedArguments = new Map<string, Promise<ReturnValue>>();

    return async (...args: ArgumentsTuple): Promise<ReturnValue> => {
        const serializedArgumentsKey = JSON.stringify(args);
        const existingPromise = cacheBySerializedArguments.get(serializedArgumentsKey);
        if (existingPromise !== undefined) {
            return existingPromise;
        }

        const dedupedPromise = functionToDedupe(...args);
        cacheBySerializedArguments.set(serializedArgumentsKey, dedupedPromise);
        return await dedupedPromise.finally(() => {
            cacheBySerializedArguments.delete(serializedArgumentsKey);
        });
    };
}

/**
 * Factory function to create PILLAR API client instances with custom fetch/headers.
 */
export function createPillarApiWithOptions(options: PillarApiClientOptions) {
    const baseUrl: string = options.baseUrl ?? '/api';
    const onSessionExpired: (() => void) | undefined = options.onSessionExpired;
    const fetchImplementation: FetchImplementation = (...args: FetchArguments) =>
        globalThis.fetch(...args);
    const getRequestHeaders: (() => HttpRequestHeaders) | undefined = options.getRequestHeaders;
    const onResponse: ((response: Response) => void) | undefined = options.onResponse;
    const onTraceContext: ((traceHeaderValue: string) => void) | undefined = options.onTraceContext;
    const onValidationError: ((error: ApiValidationError) => void) | undefined =
        options.onValidationError;
    const traceHeaderSchema: z.ZodType<string> = z.string().min(1);
    const httpPermanentRedirects: Record<string, URL> = {};
    // Make a type-safe API request with zod validation
    async function fetchPillarAPI<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options: {
            method?: string;
            body?: unknown;
            headers?: HttpRequestHeaders;
            handle401AsSessionExpired?: boolean;
        } = {}
    ): Promise<T> {
        const defaultHeaders: HttpRequestHeaders = getRequestHeaders ? getRequestHeaders() : {};
        const {
            method = 'GET',
            body,
            headers = {},
            handle401AsSessionExpired = true,
        } = options;
        const normalizedMethod = method.toUpperCase();
        const locationHeaderSchema: z.ZodType<string> = z.string().min(1);
        const isMultipartFormDataBody = isFormDataBody(body);
        const requestHeaders: Record<string, string> = {
            Accept: 'application/json',
            ...defaultHeaders,
            ...headers,
        };
        if (!isMultipartFormDataBody) {
            requestHeaders['Content-Type'] = 'application/json';
        }

        const requestInit: FetchRequestInit = {
            method: normalizedMethod,
            headers: requestHeaders,
            body:
                body === undefined
                    ? undefined
                    : isMultipartFormDataBody
                      ? body
                      : JSON.stringify(body),
            credentials: 'include', // Important for session cookies
        };

        // `baseUrl` may be:
        // - absolute URL (https://example.com/api)
        // - relative path ("/api") in browser contexts (resolved against location.origin)
        // Absolute URLs may be http(s). Relative URLs require a browser-like environment.
        let baseUrlForResolution: string = baseUrl;
        if (baseUrlForResolution.startsWith('/')) {
            // Note: api-types is Node-targeted, so `globalThis.location` is not present in typings.
            // We still support relative baseUrls ("/api") in browser-like runtimes by reading location.origin.
            // eslint-disable-next-line no-restricted-syntax
            const locationValue: unknown = (globalThis as unknown as Record<string, unknown>)
                .location;
            const locationParseResult = z
                .object({ origin: z.string().min(1) })
                .passthrough()
                .safeParse(locationValue);
            if (!locationParseResult.success) {
                throw new Error(
                    `Invalid baseUrl "${baseUrlForResolution}": relative baseUrl requires globalThis.location.origin`
                );
            }
            baseUrlForResolution = `${locationParseResult.data.origin}${baseUrlForResolution}`;
        } else if (
            !baseUrlForResolution.startsWith('https://') &&
            !baseUrlForResolution.startsWith('http://')
        ) {
            throw new Error(
                `Invalid baseUrl "${baseUrlForResolution}": must start with "https://", "http://", or "/"`
            );
        }

        const fullPath: string = endpoint.startsWith('/')
            ? `${baseUrlForResolution}${endpoint}`
            : `${baseUrlForResolution}/${endpoint}`;
        const requestUrlAsURL: URL = new URL(fullPath);
        const requestUrl: string = requestUrlAsURL.toString();
        let currentUrlAsURL: URL = httpPermanentRedirects[requestUrl] ?? requestUrlAsURL;
        let response: Response | null = null;
        const maxRedirects = 3;
        let redirectIsTransient: boolean = false;
        for (let redirectCount = 0; redirectCount <= maxRedirects; redirectCount += 1) {
            response = await fetchImplementation(currentUrlAsURL, requestInit);
            if (onResponse) {
                onResponse(response);
            }
            if (response.status >= 300 && response.status < 400) {
                // Consume redirect response body to avoid leaving the stream open
                await response.text();
                const locationHeaderValue: string | null = response.headers.get('location');
                if (locationHeaderValue === null) {
                    throw new ApiError(
                        response.status,
                        response.statusText,
                        'Redirect response missing Location header'
                    );
                }
                const locationParseResult = locationHeaderSchema.safeParse(locationHeaderValue);
                if (!locationParseResult.success) {
                    throw new ApiError(
                        response.status,
                        response.statusText,
                        'Redirect response has invalid Location header'
                    );
                }
                // Resolve Location header relative to the current request URL (RFC 7231)
                // Location can be absolute (http://example.com/path) or relative (/path, ../path)
                // If relative, resolve against the request URL that generated the redirect
                currentUrlAsURL = new URL(locationParseResult.data, currentUrlAsURL);
                redirectIsTransient = redirectIsTransient || ![301, 308].includes(response.status);
                if (!redirectIsTransient) {
                    httpPermanentRedirects[requestUrl] = currentUrlAsURL;
                }
                continue;
            }
            if (response.status >= 400) {
                // The redirection might be stale and should be forgotten
                delete httpPermanentRedirects[requestUrl];
            }
            break;
        }
        if (response === null) {
            throw new ApiError(500, 'Invalid Response', 'No response received from server');
        }

        const traceHeaderValue: string | null = response.headers.get('x-cloud-trace-context');
        if (traceHeaderValue !== null) {
            const traceHeaderParseResult: SafeParseResult<string> =
                traceHeaderSchema.safeParse(traceHeaderValue);
            if (!traceHeaderParseResult.success) {
                const validationError: ApiTraceHeaderValidationError = {
                    kind: 'trace-header',
                    headerValue: traceHeaderValue,
                    issues: traceHeaderParseResult.error.issues,
                };
                if (onValidationError) {
                    onValidationError(validationError);
                }
                throw new ApiError(500, 'Invalid Response', 'Invalid X-Cloud-Trace-Context header');
            }
            if (onTraceContext) {
                onTraceContext(traceHeaderParseResult.data);
            }
        }

        const responseBody = await response.text();

        // Handle 401 Unauthorized (session expired)
        if (response.status === 401) {
            if (onSessionExpired && handle401AsSessionExpired) {
                onSessionExpired();
            }
            // Try to extract error message from response body
            const errorMessage = extractErrorMessage(
                responseBody,
                response,
                response.status,
                response.statusText
            );
            throw new ApiError(response.status, response.statusText, errorMessage);
        }

        // Handle error responses - parse error message from payload
        if (!response.ok) {
            const errorMessage = extractErrorMessage(
                responseBody,
                response,
                response.status,
                response.statusText
            );
            throw new ApiError(response.status, response.statusText, errorMessage);
        }

        // Handle 204 No Content
        if (response.status === 204) {
            return schema.parse(undefined);
        }

        // Parse and validate JSON only if Content-Type indicates JSON
        if (!responseBody || responseBody.trim() === '') {
            throw new ApiError(response.status, response.statusText, 'Empty response body');
        }

        const contentType: string | null = response.headers.get('content-type');
        if (!isJsonContentType(contentType)) {
            throw new ApiError(
                response.status,
                response.statusText,
                `Expected JSON response but got Content-Type: ${contentType ?? 'missing'}`
            );
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(responseBody);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new ApiError(
                response.status,
                response.statusText,
                `Failed to parse JSON response: ${errorMessage}`
            );
        }

        // Always validate against schema (required)
        try {
            return schema.parse(parsed);
        } catch (validationError: unknown) {
            if (validationError instanceof z.ZodError) {
                const validationErrorDetails: ApiResponseValidationError = {
                    kind: 'response-body',
                    endpoint,
                    issues: validationError.issues,
                    response: parsed,
                };
                if (onValidationError) {
                    onValidationError(validationErrorDetails);
                }
                throw new ApiError(
                    500,
                    'Invalid Response',
                    `API response validation failed: ${validationError.issues.map((issue: z.core.$ZodIssue) => issue.message).join(', ')}`
                );
            }
            throw validationError;
        }
    }

    const requestWithSchema = async <T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options?: {
            method?: string;
            body?: unknown;
            headers?: HttpRequestHeaders;
            handle401AsSessionExpired?: boolean;
        }
    ): Promise<T> => {
        const requestOptions = options ?? {};
        return fetchPillarAPI(endpoint, schema, requestOptions);
    };

    const requestTextWithSchema = async (
        endpoint: string,
        schema: z.ZodSchema<string>,
        options?: {
            method?: string;
            headers?: HttpRequestHeaders;
            handle401AsSessionExpired?: boolean;
        }
    ): Promise<string> => {
        const requestOptions = options ?? {};
        const normalizedEndpoint: string = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const url: string = baseUrl.endsWith('/')
            ? `${baseUrl.slice(0, -1)}${normalizedEndpoint}`
            : `${baseUrl}${normalizedEndpoint}`;
        const headers: HttpRequestHeaders = requestOptions.headers ?? {};
        const method: string = requestOptions.method ?? 'GET';

        const response = await fetchImplementation(url, {
            method,
            credentials: 'include',
            headers,
        });

        if (response.status === 401 && requestOptions.handle401AsSessionExpired !== false) {
            onSessionExpired?.();
        }

        if (!response.ok) {
            const errorText: string = await response.text();
            const errorMessage: string = errorText.trim().length > 0 ? errorText : response.statusText;
            throw new ApiError(response.status, response.statusText, errorMessage);
        }

        const responseBody: string = await response.text();
        if (responseBody.trim().length === 0) {
            throw new ApiError(response.status, response.statusText, 'Empty response body');
        }

        return schema.parse(responseBody);
    };

    const authApi = {
        login: (data: LoginRequest) =>
            fetchPillarAPI('/portal/v1/user_management/any_user/login', LoginResponseSchema, {
                method: 'POST',
                body: data,
                handle401AsSessionExpired: false,
            }),

        completeCurrentCustomerPaymentLinkAttachment: (
            data: CompleteCustomerPaymentLinkAttachmentRequest
        ) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/complete_customer_payment_link_attachment',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: CompleteCustomerPaymentLinkAttachmentRequestSchema.parse(data),
                }
            ),

        verifyTotp: (data: VerifyTotpRequest) =>
            fetchPillarAPI(
                '/portal/v1/user_management/any_user/verify_totp',
                VerifyTotpResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        logout: () =>
            fetchPillarAPI('/portal/v1/user_management/any_user/logout', MessageResponseSchema, {
                method: 'POST',
            }),

        getProfile: () =>
            fetchPillarAPI<SessionUserProfileDetailedResponse>(
                '/portal/v1/user_management/any_user/profile',
                SessionUserProfileDetailedResponseSchema
            ),

        getActiveRoleAssignment: () =>
            fetchPillarAPI<ActiveRoleAssignmentResponse | null>(
                '/portal/v1/user_management/any_user/active_role_assignment',
                ActiveRoleAssignmentResponseSchema.nullable()
            ),

        requestSelfVerificationEmail: dedupe(() =>
            fetchPillarAPI<SendVerificationEmailResponse>(
                '/portal/v1/user_management/any_user/request_self_verification_email',
                SendVerificationEmailResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        verifyEmail: (token: string, password?: string) =>
            fetchPillarAPI<VerifyEmailResponse>(
                '/portal/v1/user_management/any_user/verify_email',
                VerifyEmailResponseSchema,
                {
                    method: 'POST',
                    body: {
                        token,
                        ...(password !== undefined ? { password } : {}),
                    } satisfies VerifyEmailRequest,
                }
            ),

        createCustomerSelfOnboarding: (data: CreateCustomerSelfOnboardingRequest) =>
            fetchPillarAPI<CreateCustomerSelfOnboardingResponse>(
                '/portal/v1/user_management/any_user/customer_self_onboarding',
                CreateCustomerSelfOnboardingResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        requestPasswordReset: dedupe((email: string) =>
            fetchPillarAPI<RequestPasswordResetResponse>(
                '/portal/v1/user_management/any_user/request_password_reset',
                RequestPasswordResetResponseSchema,
                {
                    method: 'POST',
                    body: { email } satisfies RequestPasswordResetRequest,
                }
            )
        ),

        validatePasswordResetToken: (token: string) =>
            fetchPillarAPI<ValidatePasswordResetTokenResponse>(
                '/portal/v1/user_management/any_user/validate_reset_password_token',
                ValidatePasswordResetTokenResponseSchema,
                {
                    method: 'POST',
                    body: { token } satisfies ValidatePasswordResetTokenRequest,
                }
            ),

        resetPassword: (token: string, newPassword: string) =>
            fetchPillarAPI<ResetPasswordResponse>(
                '/portal/v1/user_management/any_user/reset_password',
                ResetPasswordResponseSchema,
                {
                    method: 'POST',
                    body: { token, newPassword } satisfies ResetPasswordRequest,
                }
            ),

        setDefaultRoleAssignment: (role_assignment_uuid: string) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/set_default_role_assignment',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: {
                        role_assignment_uuid,
                    } satisfies SetDefaultRoleAssignmentUuidRequest,
                }
            ),

        setActiveRoleAssignment: (role_assignment_uuid: string) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/set_active_role_assignment',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: {
                        role_assignment_uuid,
                    } satisfies SetActiveRoleAssignmentUuidRequest,
                }
            ),

        startImpersonation: (role_assignment_uuid: string) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/start_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: StartImpersonationRequestSchema.parse({
                        role_assignment_uuid,
                    }) satisfies StartImpersonationRequest,
                }
            ),

        stopImpersonation: () =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/stop_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        getImpersonationContext: () =>
            fetchPillarAPI<ImpersonationContextResponse | null>(
                '/portal/v1/user_management/any_user/impersonation_context',
                ImpersonationContextResponseSchema.nullable()
            ),

        changePassword: (data: ChangePasswordRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/change_password',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setEmails: (data: SetUserEmailsRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/set_emails',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        requestAddEmailVerification: (data: RequestAddEmailVerificationRequest) =>
            fetchPillarAPI<RequestAddEmailVerificationResponse>(
                '/portal/v1/user_management/any_user/request_add_email_verification',
                RequestAddEmailVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddEmailVerificationRequestSchema.parse(data),
                }
            ),

        setupTOTP: dedupe(() =>
            fetchPillarAPI<SetupTotpResponse>(
                '/portal/v1/user_management/any_user/setup_totp',
                SetupTotpResponseSchema
            )
        ),

        enableTOTP: (data: EnableTotpRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/enable_totp',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        disableTOTP: () =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_totp',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        generateBackupCodes: dedupe(() =>
            fetchPillarAPI<GenerateBackupCodesResponse>(
                '/portal/v1/user_management/any_user/generate_backup_codes',
                GenerateBackupCodesResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        getSessions: () =>
            fetchPillarAPI<GetSessionsResponse>(
                '/portal/v1/user_management/any_user/sessions',
                GetSessionsResponseSchema
            ),

        expireSession: (data: ExpireSessionUuidRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/expire_session',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ExpireSessionUuidRequestSchema.parse(data),
                }
            ),

        getAllSessions: () =>
            fetchPillarAPI<GetSessionsResponse>(
                '/portal/v1/user_management/admin/sessions',
                GetSessionsResponseSchema
            ),

        listAllSessionsPage: (params: SessionsSearchRequest = {}) => {
            const requestBody: SessionsSearchRequest = SessionsSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListSessionsPageResponse>(
                '/portal/v1/user_management/admin/sessions/search',
                ListSessionsPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        expireSessionAdmin: (data: ExpireUserSessionRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/admin/expire_session',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ExpireUserSessionRequestSchema.parse(data),
                }
            ),

        setSessionConsoleAdmin: (data: UpdateSessionConsoleRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/admin/set_session_console',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: UpdateSessionConsoleRequestSchema.parse(data),
                }
            ),

        deleteMyAccount: (password: string) =>
            fetchPillarAPI<DeleteMyAccountResponse>(
                '/portal/v1/user_management/any_user',
                DeleteMyAccountResponseSchema,
                {
                    method: 'DELETE',
                    body: { password } satisfies DeleteMyAccountRequest,
                }
            ),
    };

    const userApi = {
        listSingleUseTokensAdmin: (request: SingleUseTokensSearchRequest) =>
            fetchPillarAPI<ListSingleUseTokensResponse>(
                '/portal/v1/user_management/pillar_admin/single_use_tokens/search',
                ListSingleUseTokensResponseSchema,
                {
                    method: 'POST',
                    body: SingleUseTokensSearchRequestSchema.parse(request),
                }
            ),

        listSingleUseTokensByUserAdmin: (
            userUuid: string,
            request: SingleUseTokensSearchRequest
        ) =>
            fetchPillarAPI<ListSingleUseTokensResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/single_use_tokens/search`,
                ListSingleUseTokensResponseSchema,
                {
                    method: 'POST',
                    body: SingleUseTokensSearchRequestSchema.parse(request),
                }
            ),

        expireSingleUseTokenAdmin: (singleUseAuthTokenUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/single_use_tokens/${singleUseAuthTokenUuid}/expire`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        resendSingleUseTokenAdmin: (singleUseAuthTokenUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/single_use_tokens/${singleUseAuthTokenUuid}/resend`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        setUserEmailsAdmin: (userUuid: string, data: SetUserEmailsRequest) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/set_emails`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        requestAddEmailVerificationAdmin: (
            userUuid: string,
            data: RequestAddEmailVerificationRequest
        ) =>
            fetchPillarAPI<RequestAddEmailVerificationResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/request_add_email_verification`,
                RequestAddEmailVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddEmailVerificationRequestSchema.parse(data),
                }
            ),

        resetPasswordAdmin: dedupe((userUuid: string, newPassword: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/reset_password`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: {
                        new_password: newPassword,
                    } satisfies ResetPasswordAdminRequest,
                }
            )
        ),

        sendVerificationEmailAdmin: dedupe((userUuid: string) =>
            fetchPillarAPI<SendVerificationEmailResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/send_verification_email`,
                SendVerificationEmailResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        getUser: (userUuid: string) =>
            fetchPillarAPI<UserProfileDetailedPublicResponse>(
                `/portal/v1/user_management/user/${userUuid}`,
                UserProfileDetailedPublicResponseSchema
            ),

        createUser: dedupe((data: CreateOrganizationalUserRequest) =>
            fetchPillarAPI<CreateOrganizationalUserResponse>(
                '/portal/v1/user_management/pillar_admin/create_ouser',
                CreateOrganizationalUserResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        createIUser: dedupe((data: CreateUserRequest) =>
            fetchPillarAPI<CreateInternalUserResponse>(
                '/portal/v1/user_management/superadmin/create_iuser',
                CreateInternalUserResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        updateUser: dedupe((userUuid: string, data: UpdateUserRequest) =>
            fetchPillarAPI<UpdateUserResponse>(
                `/portal/v1/user_management/pillar_admin/update_user/${userUuid}`,
                UpdateUserResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            )
        ),

        deleteUser: (userUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/superadmin/delete_user/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        undeleteUser: (userUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/superadmin/undelete_user/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        assignRole: (data: AssignRoleRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/pillar_admin/assign_role',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        revokeRole: (data: RevokeRoleRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/pillar_admin/revoke_role',
                MessageResponseSchema,
                {
                    method: 'DELETE',
                    body: data,
                }
            ),

        updateRoleAssignment: (
            roleAssignmentUuid: string,
            data: Omit<UpdateRoleAssignmentRequest, 'role_assignment_uuid'>
        ) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/role_assignments/${roleAssignmentUuid}`,
                MessageResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        setDefaultRoleAssignment: (userUuid: string, roleAssignmentUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/set_default_role_assignment/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'PATCH',
                    body: {
                        role_assignment_uuid: roleAssignmentUuid,
                    } satisfies SetDefaultRoleAssignmentRequest,
                }
            ),

        searchUsers: (params: UserOmnisearchPageRequest) => {
            const queryParams = new URLSearchParams();
            if (params.q !== undefined) {
                queryParams.set('q', params.q);
            }
            if (params.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params.cursor !== undefined) {
                queryParams.set('cursor', params.cursor.toString());
            }
            if (params.organization_uuid !== undefined) {
                queryParams.set('organization_uuid', params.organization_uuid);
            }
            return fetchPillarAPI<UserOmnisearchPageResponse>(
                `/portal/v1/user_management/users/search?${queryParams.toString()}`,
                UserOmnisearchPageResponseSchema
            );
        },

        listInternalUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/internal_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listIsoUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/iso_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listMerchantUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/merchant_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listCustomerUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/customer_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),
    };

    const organizationApi = {
        searchOrganizations: (params: OrganizationOmnisearchSearchRequest) => {
            const requestBody: OrganizationOmnisearchSearchRequest =
                OrganizationOmnisearchSearchRequestSchema.parse(params);
            return fetchPillarAPI<OrganizationOmnisearchPageResponse>(
                '/portal/v1/user_management/organizations/search',
                OrganizationOmnisearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        listSubsidiaryIsosPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            return fetchPillarAPI<ListSubsidiaryIsosPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/subsidiary_isos?${queryParams.toString()}`,
                ListSubsidiaryIsosPageResponseSchema
            );
        },

        listSubsidiaryMerchantsPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            return fetchPillarAPI<ListSubsidiaryMerchantsPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/subsidiary_merchants?${queryParams.toString()}`,
                ListSubsidiaryMerchantsPageResponseSchema
            );
        },

        listDirectOrganizationUsersPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            return fetchPillarAPI<ListDirectOrganizationUsersPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users?${queryParams.toString()}`,
                ListDirectOrganizationUsersPageResponseSchema
            );
        },

        previewOrganizationUsersCsvImport: (
            organizationUuid: string,
            body: PreviewOrganizationUsersCsvImportRequest
        ) =>
            fetchPillarAPI<PreviewOrganizationUsersCsvImportResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users/import-csv/preview`,
                PreviewOrganizationUsersCsvImportResponseSchema,
                {
                    method: 'POST',
                    body: PreviewOrganizationUsersCsvImportRequestSchema.parse(body),
                }
            ),

        applyOrganizationUsersCsvImport: (
            organizationUuid: string,
            body: ApplyOrganizationUsersCsvImportRequest
        ) =>
            fetchPillarAPI<ApplyOrganizationUsersCsvImportResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users/import-csv/apply`,
                ApplyOrganizationUsersCsvImportResponseSchema,
                {
                    method: 'POST',
                    body: ApplyOrganizationUsersCsvImportRequestSchema.parse(body),
                }
            ),

        listDocumentTemplates: (organizationUuid: string) =>
            fetchPillarAPI<ListOrganizationDocumentTemplatesResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates`,
                ListOrganizationDocumentTemplatesResponseSchema
            ),

        validateDocumentTemplate: (
            organizationUuid: string,
            body: ValidateOrganizationDocumentTemplateRequest
        ) =>
            fetchPillarAPI<ValidateOrganizationDocumentTemplateResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/validate`,
                ValidateOrganizationDocumentTemplateResponseSchema,
                {
                    method: 'POST',
                    body: ValidateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        createDocumentTemplate: (
            organizationUuid: string,
            body: CreateOrganizationDocumentTemplateRequest
        ) =>
            fetchPillarAPI<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'POST',
                    body: CreateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        updateDocumentTemplate: (
            organizationUuid: string,
            documentTemplateUuid: string,
            body: UpdateOrganizationDocumentTemplateRequest
        ) =>
            fetchPillarAPI<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/${documentTemplateUuid}`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'PATCH',
                    body: UpdateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        setDocumentTemplateActive: (
            organizationUuid: string,
            documentTemplateUuid: string,
            body: SetOrganizationDocumentTemplateActiveRequest
        ) =>
            fetchPillarAPI<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/${documentTemplateUuid}/active`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'PATCH',
                    body: SetOrganizationDocumentTemplateActiveRequestSchema.parse(body),
                }
            ),

        getOrganization: (organizationUuid: string) =>
            fetchPillarAPI<GetOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                GetOrganizationResponseSchema
            ),

        createIso: dedupe((data: CreateIsoRequest) =>
            fetchPillarAPI<OrganizationCreateIsoResponse>(
                '/portal/v1/user_management/pillar_admin/create_iso',
                CreateIsoResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        createMerchant: dedupe((data: CreateMerchantRequest) =>
            fetchPillarAPI<OrganizationCreateMerchantResponse>(
                '/portal/v1/user_management/pillar_admin/create_merchant',
                CreateMerchantResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        assignMerchantToIso: (data: AssignMerchantToIsoRequest) =>
            fetchPillarAPI<AssignMerchantToIsoResponse>(
                '/portal/v1/user_management/pillar_admin/assign_merchant_to_iso',
                AssignMerchantToIsoResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setOrganizationParent: (data: SetOrganizationParentRequest) =>
            fetchPillarAPI<SetOrganizationParentResponse>(
                '/portal/v1/user_management/pillar_admin/set_organization_parent',
                SetOrganizationParentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updateOrganization: dedupe((organizationUuid: string, data: UpdateOrganizationRequest) =>
            fetchPillarAPI<UpdateOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                UpdateOrganizationResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            )
        ),

        deleteOrganization: (organizationUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        undeleteOrganization: (organizationUuid: string) =>
            fetchPillarAPI<UpdateOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/undelete`,
                UpdateOrganizationResponseSchema,
                {
                    method: 'POST',
                }
            ),

        exportMerchantBackup: (organizationUuid: string) =>
            fetchPillarAPI<ExportMerchantBackupResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-backup/export`,
                ExportMerchantBackupResponseSchema
            ),

        getMerchantChecklist: (organizationUuid: string) =>
            fetchPillarAPI<GetMerchantChecklistResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-checklist`,
                GetMerchantChecklistResponseSchema
            ),

        getMerchantChecklistCheck: (
            organizationUuid: string,
            checkCode: MerchantChecklistCheckCode
        ) =>
            fetchPillarAPI<GetMerchantChecklistCheckResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-checklist/check/${checkCode}`,
                GetMerchantChecklistCheckResponseSchema
            ),

        restoreMerchantBackupDryRun: (payload: RestoreMerchantBackupDryRunRequest) =>
            fetchPillarAPI<RestoreMerchantBackupDryRunResponse>(
                '/portal/v1/user_management/merchant-backup/restore/dry-run',
                RestoreMerchantBackupDryRunResponseSchema,
                {
                    method: 'POST',
                    body: RestoreMerchantBackupDryRunRequestSchema.parse(payload),
                }
            ),

        restoreMerchantBackupApply: (payload: RestoreMerchantBackupApplyRequest) =>
            fetchPillarAPI<RestoreMerchantBackupApplyResponse>(
                '/portal/v1/user_management/merchant-backup/restore/apply',
                RestoreMerchantBackupApplyResponseSchema,
                {
                    method: 'POST',
                    body: RestoreMerchantBackupApplyRequestSchema.parse(payload),
                }
            ),
    };

    const customerApi = {
        listCustomerUsersPage: (params: CustomerUsersSearchRequest = {}) => {
            const requestBody: CustomerUsersSearchRequest =
                CustomerUsersSearchRequestSchema.parse(params);
            return fetchPillarAPI<CustomerUsersPageResponse>(
                '/portal/v1/user_management/customer-users/search',
                CustomerUsersPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        searchCustomers: (params: MerchantCustomerRecordSearchRequest = {}) => {
            const requestBody: MerchantCustomerRecordSearchRequest =
                MerchantCustomerRecordSearchRequestSchema.parse(params);
            return fetchPillarAPI<MerchantCustomerRecordSearchPageResponse>(
                '/portal/v1/user_management/customers/search',
                MerchantCustomerRecordSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getCustomer: (merchantCustomerRecordUuid: string) =>
            fetchPillarAPI<MerchantCustomerRecordDetailsResponse>(
                `/portal/v1/user_management/customers/${merchantCustomerRecordUuid}`,
                MerchantCustomerRecordDetailsResponseSchema
                ),

        updateCustomer: dedupe((merchantCustomerRecordUuid: string, data: UpdateMerchantCustomerRecordRequest) =>
            fetchPillarAPI<MerchantCustomerRecordDetailsResponse>(
                `/portal/v1/user_management/customers/${merchantCustomerRecordUuid}`,
                MerchantCustomerRecordDetailsResponseSchema,
                {
                    method: 'PATCH',
                    body: UpdateMerchantCustomerRecordRequestSchema.parse(data),
                }
            )
        ),
    };

    const parseLogicalTerminalUuidPathSegment = (logicalTerminalUuid: string): string =>
        LogicalTerminalUuidParamSchema.parse(logicalTerminalUuid);

    const paymentsInfrastructureApi = {
        searchProcessors: (params: ProcessorSearchRequest = {}) => {
            const parseResult = ProcessorSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(`Invalid processor search request: ${parseResult.error.message}`);
            }
            return fetchPillarAPI<ProcessorSearchPageResponse>(
                '/portal/v1/payments_infrastructure/processors/search',
                ProcessorSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getProcessor: (processorName: string) =>
            fetchPillarAPI<GetProcessorResponse>(
                `/portal/v1/payments_infrastructure/processors/${encodeURIComponent(processorName)}`,
                GetProcessorResponseSchema
            ),

        createProcessor: dedupe((payload: CreateProcessorRequest) => {
            const parseResult = CreateProcessorRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(`Invalid create processor request: ${parseResult.error.message}`);
            }
            return fetchPillarAPI<GetProcessorResponse>(
                '/portal/v1/payments_infrastructure/processors',
                GetProcessorResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateProcessor: dedupe((processorName: string, payload: UpdateProcessorRequest) => {
            const parseResult = UpdateProcessorRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid update processor request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetProcessorResponse>(
                `/portal/v1/payments_infrastructure/processors/${encodeURIComponent(processorName)}`,
                GetProcessorResponseSchema,
                { method: 'PATCH', body: parseResult.data }
            );
        }),

        searchMerchantAccounts: (params: MerchantAccountSearchRequest = {}) => {
            const parseResult = MerchantAccountSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid merchant account search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<MerchantAccountSearchPageResponse>(
                '/portal/v1/payments_infrastructure/merchant_accounts/search',
                MerchantAccountSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getMerchantAccount: (merchantAccountUuid: string) =>
            fetchPillarAPI<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}`,
                GetMerchantAccountResponseSchema
            ),

        createMerchantAccount: dedupe((payload: CreateMerchantAccountRequest) => {
            const parseResult = CreateMerchantAccountRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create merchant account request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetMerchantAccountResponse>(
                '/portal/v1/payments_infrastructure/merchant_accounts',
                GetMerchantAccountResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateMerchantAccount: dedupe((merchantAccountUuid: string, payload: UpdateMerchantAccountRequest) => {
            const parseResult = UpdateMerchantAccountRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid update merchant account request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}`,
                GetMerchantAccountResponseSchema,
                { method: 'PATCH', body: parseResult.data }
            );
        }),

        deleteMerchantAccount: (merchantAccountUuid: string) =>
            fetchPillarAPI<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}`,
                GetMerchantAccountResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteMerchantAccount: (merchantAccountUuid: string) =>
            fetchPillarAPI<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}/undelete`,
                GetMerchantAccountResponseSchema,
                { method: 'POST' }
            ),

        searchLogicalTerminals: (params: LogicalTerminalSearchRequest = {}) => {
            const parseResult = LogicalTerminalSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid logical terminal search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<LogicalTerminalSearchPageResponse>(
                '/portal/v1/payments_infrastructure/logical_terminals/search',
                LogicalTerminalSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        searchMerchantOrganizations: (params: MerchantOrganizationSearchRequest) => {
            const parseResult = MerchantOrganizationSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid merchant organization search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<MerchantOrganizationSearchPageResponse>(
                '/portal/v1/payments_infrastructure/merchant_organizations/search',
                MerchantOrganizationSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getLogicalTerminal: (logicalTerminalUuid: string) =>
            fetchPillarAPI<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}`,
                GetLogicalTerminalResponseSchema
            ),

        createLogicalTerminal: dedupe((payload: CreateLogicalTerminalRequest) => {
            const parseResult = CreateLogicalTerminalRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create logical terminal request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetLogicalTerminalResponse>(
                '/portal/v1/payments_infrastructure/logical_terminals',
                GetLogicalTerminalResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateLogicalTerminal: dedupe((logicalTerminalUuid: string, payload: UpdateLogicalTerminalRequest) => {
            const parseResult = UpdateLogicalTerminalRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid update logical terminal request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}`,
                GetLogicalTerminalResponseSchema,
                { method: 'PATCH', body: parseResult.data }
            );
        }),

        deleteLogicalTerminal: (logicalTerminalUuid: string) =>
            fetchPillarAPI<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}`,
                GetLogicalTerminalResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteLogicalTerminal: (logicalTerminalUuid: string) =>
            fetchPillarAPI<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}/undelete`,
                GetLogicalTerminalResponseSchema,
                { method: 'POST' }
            ),

        previewLogicalTerminalOrganizationChange: (
            logicalTerminalUuid: string,
            payload: PreviewLogicalTerminalOrganizationChangeRequest
        ) => {
            const parseResult =
                PreviewLogicalTerminalOrganizationChangeRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid logical terminal organization preview request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<PreviewLogicalTerminalOrganizationChangeResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}/organization-change-preview`,
                PreviewLogicalTerminalOrganizationChangeResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        searchTerminalDevices: (params: TerminalDeviceSearchRequest = {}) => {
            const parseResult = TerminalDeviceSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid terminal device search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<TerminalDeviceSearchPageResponse>(
                '/portal/v1/payments_infrastructure/terminal_devices/search',
                TerminalDeviceSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getTerminalDevice: (terminalDeviceUuid: string) =>
            fetchPillarAPI<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}`,
                GetTerminalDeviceResponseSchema
            ),

        createTerminalDevice: dedupe((payload: CreateTerminalDeviceRequest) => {
            const parseResult = CreateTerminalDeviceRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create terminal device request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetTerminalDeviceResponse>(
                '/portal/v1/payments_infrastructure/terminal_devices',
                GetTerminalDeviceResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateTerminalDevice: dedupe((terminalDeviceUuid: string, payload: UpdateTerminalDeviceRequest) => {
            const parseResult = UpdateTerminalDeviceRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid update terminal device request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}`,
                GetTerminalDeviceResponseSchema,
                { method: 'PATCH', body: parseResult.data }
            );
        }),

        deleteTerminalDevice: (terminalDeviceUuid: string) =>
            fetchPillarAPI<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}`,
                GetTerminalDeviceResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteTerminalDevice: (terminalDeviceUuid: string) =>
            fetchPillarAPI<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}/undelete`,
                GetTerminalDeviceResponseSchema,
                { method: 'POST' }
            ),
    };

    const sambaConfigApi = {
        getIsoAdminRoleAssignmentConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                ConfigResponseSchema
            ),

        getIsoAdminRoleAssignmentOrgConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/org_config`,
                ConfigResponseSchema
            ),

        updateIsoAdminRoleAssignmentConfig: (
            roleAssignmentUuid: string,
            config: ConfigResponse
        ) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                ConfigResponseSchema,
                {
                    method: 'PUT',
                    body: { config } satisfies UpdateConfigRequest,
                }
            ),

        getIsoAdminOrgConfig: (organizationUuid: string) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/iso_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema
            ),

        updateIsoAdminOrgConfig: (organizationUuid: string, config: ConfigResponse) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/iso_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema,
                {
                    method: 'PUT',
                    body: { config } satisfies UpdateConfigRequest,
                }
            ),

        getRoleAssignmentConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/pillar_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                ConfigResponseSchema
            ),

        updateRoleAssignmentConfig: (roleAssignmentUuid: string, config: ConfigResponse) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/pillar_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                ConfigResponseSchema,
                {
                    method: 'PUT',
                    body: { config } satisfies UpdateConfigRequest,
                }
            ),

        getOrgConfig: (organizationUuid: string) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/pillar_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema
            ),

        updateOrgConfig: (organizationUuid: string, config: ConfigResponse) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/pillar_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema,
                {
                    method: 'PUT',
                    body: { config } satisfies UpdateConfigRequest,
                }
            ),

        getMyUserConfig: () =>
            fetchPillarAPI<ConfigResponse>(
                '/portal/v1/samba_config/any_user/user',
                ConfigResponseSchema
            ),

        getMyOrgConfig: () =>
            fetchPillarAPI<ConfigResponse>(
                '/portal/v1/samba_config/any_user/org',
                ConfigResponseSchema
            ),

        getMyEffectiveConfig: () =>
            fetchPillarAPI<ConfigResponse>(
                '/portal/v1/samba_config/any_user/effective',
                ConfigResponseSchema
            ),

        getMyCombinedConfig: () =>
            fetchPillarAPI<CombinedConfigResponse>(
                '/portal/v1/samba_config/any_user/combined',
                CombinedConfigResponseSchema
            ),

        getMyRoleAssignmentUserConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/any_user/role_assignment/${roleAssignmentUuid}/user_config`,
                ConfigResponseSchema
            ),

        getMyRoleAssignmentOrgConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<ConfigResponse>(
                `/portal/v1/samba_config/any_user/role_assignment/${roleAssignmentUuid}/org_config`,
                ConfigResponseSchema
            ),

        patchMyActiveRoleAssignmentUserRoleAssignmentConfig: (
            request: PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest
        ) =>
            fetchPillarAPI<ConfigResponse>(
                '/portal/v1/samba_config/any_user/active_role_assignment/user_role_assignment_config',
                ConfigResponseSchema,
                {
                    method: 'PATCH',
                    body:
                        PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema.parse(
                            request
                        ),
                }
            ),

        getCombinedConfigForUser: (userId: number) =>
            fetchPillarAPI<CombinedConfigResponse>(
                `/portal/v1/samba_config/pillar_admin/effective_config/${userId}`,
                CombinedConfigResponseSchema
            ),

        getCombinedConfigForSessionOther: (sessionUuid: string) =>
            fetchPillarAPI<CombinedConfigResponse>(
                `/portal/v1/samba_config/admin/session/${sessionUuid}/config/combined`,
                CombinedConfigResponseSchema
            ),

        getCombinedConfigForSessionSelf: (auditId: number) =>
            fetchPillarAPI<CombinedConfigResponse>(
                `/portal/v1/samba_config/any_user/session/${auditId}/config/combined`,
                CombinedConfigResponseSchema
            ),
    };

    const paymentsApi = {
        getVaultTransactions: (params: GetVaultTransactionsSearchRequest = {}) => {
            const requestBody: GetVaultTransactionsSearchRequest =
                GetVaultTransactionsSearchRequestSchema.parse(params);
            return fetchPillarAPI<GetVaultTransactionsResponse>(
                '/portal/v1/payments/vault/transactions/search',
                GetVaultTransactionsResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getPaymentAuditLog: (params: GetPaymentAuditLogSearchRequest) => {
            const requestBody: GetPaymentAuditLogSearchRequest =
                GetPaymentAuditLogSearchRequestSchema.parse(params);
            return fetchPillarAPI<GetPaymentAuditLogResponse>(
                '/portal/v1/payments/audit-log/search',
                GetPaymentAuditLogResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getSavedCardTokens: (
            params: GetSavedCardTokensRequest
        ) => {
            const queryParams = new URLSearchParams({
                customer_name: params.customer_name,
                phone_number: params.phone_number,
                ipw_system_id: params.ipw_system_id.toString(),
            });
            return fetchPillarAPI<GetSavedCardTokensResponse>(
                `/portal/v1/payments/method/card-token?${queryParams.toString()}`,
                GetSavedCardTokensResponseSchema
            );
        },

        getDelegateToken: dedupe((data: GetDelegateTokenRequest) =>
            fetchPillarAPI<GetDelegateTokenResponse>(
                '/portal/v1/payments/delegate-token',
                GetDelegateTokenResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        binLookup: dedupe((data: BinLookupRequest) =>
            fetchPillarAPI<BinLookupResponse>(
                '/portal/v1/payments/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        getPaymentConfiguration: (params: GetPaymentConfigurationRequest) => {
            const validatedParams: GetPaymentConfigurationRequest =
                GetPaymentConfigurationRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                terminal_device_uuid: validatedParams.terminal_device_uuid,
            });
            return fetchPillarAPI<PaymentConfigurationResponse>(
                `/portal/v1/payments/payment-configuration?${queryParams.toString()}`,
                PaymentConfigurationResponseSchema
            );
        },

        createDraftPaymentRequest: (data: CreateDraftPaymentRequestRequest) => {
            const validatedData: CreateDraftPaymentRequestRequest =
                CreateDraftPaymentRequestRequestSchema.parse(data);
            return fetchPillarAPI<CreateDraftPaymentRequestResponse>(
                '/portal/v1/payments/payment-requests/draft',
                CreateDraftPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        getPaymentRequestOwnerCandidates: () =>
            fetchPillarAPI<PaymentRequestOwnerCandidatesResponse>(
                '/portal/v1/payments/payment-requests/owner-candidates',
                PaymentRequestOwnerCandidatesResponseSchema
            ),

        uploadDraftPaymentRequestDocument: (paymentRequestUuid: string, file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            return fetchPillarAPI<UploadPaymentRequestDocumentResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/documents/upload`,
                UploadPaymentRequestDocumentResponseSchema,
                {
                    method: 'POST',
                    body: formData,
                }
            );
        },

        finalizeSendPaymentRequest: (data: FinalizeSendPaymentRequestRequest) => {
            const validatedData: FinalizeSendPaymentRequestRequest =
                FinalizeSendPaymentRequestRequestSchema.parse(data);
            return fetchPillarAPI<FinalizeSendPaymentRequestResponse>(
                '/portal/v1/payments/payment-requests/finalize-send',
                FinalizeSendPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        listMerchantPaymentRequests: (params: MerchantPaymentRequestSearchRequest = {}) => {
            const requestBody: MerchantPaymentRequestSearchRequest =
                MerchantPaymentRequestSearchRequestSchema.parse(params);
            return fetchPillarAPI<MerchantPaymentRequestListResponse>(
                '/portal/v1/payments/payment-requests/search',
                MerchantPaymentRequestListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getMerchantPaymentRequestDetails: (paymentRequestUuid: string) =>
            fetchPillarAPI<PaymentRequestDetailsView>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}`,
                PaymentRequestDetailsViewSchema
            ),

        getMerchantPaymentRequestDeliveryEvents: (paymentRequestUuid: string) =>
            fetchPillarAPI<PaymentRequestDeliveryEventList>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/delivery-events`,
                PaymentRequestDeliveryEventListSchema
            ),

        resendPaymentRequest: (data: ResendPaymentRequestRequest) => {
            const validatedData: ResendPaymentRequestRequest =
                ResendPaymentRequestRequestSchema.parse(data);
            return fetchPillarAPI<FinalizeSendPaymentRequestResponse>(
                '/portal/v1/payments/payment-requests/resend',
                FinalizeSendPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        cancelAndReissuePaymentRequest: (
            paymentRequestUuid: string,
            data: CancelAndReissuePaymentRequestRequest
        ) => {
            const validatedData: CancelAndReissuePaymentRequestRequest =
                CancelAndReissuePaymentRequestRequestSchema.parse(data);
            return fetchPillarAPI<CancelAndReissuePaymentRequestResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/cancel-and-reissue`,
                CancelAndReissuePaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        bootstrapPaymentRequestGuestSession: (tokenValue: string) =>
            fetchPillarAPI<PublicPaymentRequestView>(
                `/portal/v1/payments/public/payment-request-links/${tokenValue}/bootstrap`,
                PublicPaymentRequestViewSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentPublicPaymentRequest: () =>
            fetchPillarAPI<PublicPaymentRequestView>(
                '/portal/v1/payments/public/payment-request-links/current',
                PublicPaymentRequestViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        requestCurrentPaymentRequestReplacementLink: () =>
            fetchPillarAPI<PublicPaymentLinkActionResponse>(
                '/portal/v1/payments/public/payment-request-links/current/request-new-link',
                PublicPaymentLinkActionResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        resendCurrentReissuedPaymentRequestLink: () =>
            fetchPillarAPI<PublicPaymentLinkActionResponse>(
                '/portal/v1/payments/public/payment-request-links/current/resend-reissued-link',
                PublicPaymentLinkActionResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentEntryContext: () =>
            fetchPillarAPI<GuestPaymentEntryContextView>(
                '/portal/v1/payments/public/payment-request-links/current/payment-entry-context',
                GuestPaymentEntryContextViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentAuthenticatedPaymentEntryContext: () =>
            fetchPillarAPI<AuthenticatedPaymentEntryContextView>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment-entry-context',
                AuthenticatedPaymentEntryContextViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentSuccessView: () =>
            fetchPillarAPI<GuestPaymentSuccessView>(
                '/portal/v1/payments/public/payment-request-links/current/payment-success',
                GuestPaymentSuccessViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestDelegateToken: () =>
            fetchPillarAPI<GetDelegateTokenResponse>(
                '/portal/v1/payments/public/payment-request-links/current/delegate-token',
                GetDelegateTokenResponseSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        guestBinLookup: (data: GuestBinLookupRequest) => {
            const validatedData: GuestBinLookupRequest = GuestBinLookupRequestSchema.parse(data);
            return fetchPillarAPI<BinLookupResponse>(
                '/portal/v1/payments/public/payment-request-links/current/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentGuestCardTokenPayment: (data: GuestProcessCardTokenPaymentRequest) => {
            const validatedData: GuestProcessCardTokenPaymentRequest =
                GuestProcessCardTokenPaymentRequestSchema.parse(data);
            return fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/public/payment-request-links/current/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentAuthenticatedCardTokenPayment: (
            data: AuthenticatedProcessCardTokenPaymentRequest
        ) => {
            const validatedData: AuthenticatedProcessCardTokenPaymentRequest =
                AuthenticatedProcessCardTokenPaymentRequestSchema.parse(data);
            return fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentStoredPaymentMethodPayment: (
            data: GuestProcessStoredPaymentMethodPaymentRequest
        ) => {
            const validatedData: GuestProcessStoredPaymentMethodPaymentRequest =
                GuestProcessStoredPaymentMethodPaymentRequestSchema.parse(data);
            return fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment/stored-method',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        listCardPaymentTerminalDevices: (params: ListCardPaymentTerminalDevicesRequest) => {
            const validatedParams: ListCardPaymentTerminalDevicesRequest =
                ListCardPaymentTerminalDevicesRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                system_id: String(validatedParams.system_id),
                is_virtual: String(validatedParams.is_virtual),
                action: validatedParams.action,
            });
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            return fetchPillarAPI<ListCardPaymentTerminalDevicesResponse>(
                `/portal/v1/payments/card-payment/terminals?${queryParams.toString()}`,
                ListCardPaymentTerminalDevicesResponseSchema
            );
        },

        getAccessibleCredentialsInfo: (params: ListAccessibleCredentialsInfoRequest) => {
            const validatedParams: ListAccessibleCredentialsInfoRequest =
                ListAccessibleCredentialsInfoRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                system_id: String(validatedParams.system_id),
                is_virtual: String(validatedParams.is_virtual),
            });
            if (validatedParams.credential_type !== undefined) {
                queryParams.set('credential_type', validatedParams.credential_type);
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            return fetchPillarAPI<ListAccessibleCredentialsInfoResponse>(
                `/portal/v1/payments/info/accessible-credentials?${queryParams.toString()}`,
                ListAccessibleCredentialsInfoResponseSchema
            );
        },

        getAccessibleTerminalsInfo: (params: ListAccessibleTerminalsInfoRequest) => {
            const validatedParams: ListAccessibleTerminalsInfoRequest =
                ListAccessibleTerminalsInfoRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                system_id: String(validatedParams.system_id),
                is_virtual: String(validatedParams.is_virtual),
            });
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            return fetchPillarAPI<ListAccessibleTerminalsInfoResponse>(
                `/portal/v1/payments/info/accessible-terminals?${queryParams.toString()}`,
                ListAccessibleTerminalsInfoResponseSchema
            );
        },

        getReconcilePaymentsCandidates: (params: GetReconcilePaymentsCandidatesRequest) => {
            const queryParams = new URLSearchParams({
                customer_name: params.customer_name,
                ipw_system_id: params.ipw_system_id.toString(),
            });
            if (params.phone_number !== undefined) {
                queryParams.set('phone_number', params.phone_number);
            }
            if (params.parts_invoice_number !== undefined) {
                queryParams.set('parts_invoice_number', params.parts_invoice_number);
            }
            if (params.ro_number !== undefined) {
                queryParams.set('ro_number', params.ro_number);
            }
            if (params.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            return fetchPillarAPI<GetReconcilePaymentsCandidatesResponse>(
                `/portal/v1/payments/reconcile/candidates?${queryParams.toString()}`,
                GetReconcilePaymentsCandidatesResponseSchema
            );
        },

        getTransactionDetails: (paymentTransactionUuid: string) =>
            fetchPillarAPI<GetTransactionDetailsResponse>(
                `/portal/v1/payments/vault/transactions/${paymentTransactionUuid}`,
                GetTransactionDetailsResponseSchema
            ),

        refreshGatewayTransactionDetails: (paymentTransactionUuid: string) =>
            fetchPillarAPI<GatewayTransactionDetailsFetchResult>(
                `/portal/v1/payments/vault/transactions/${paymentTransactionUuid}/gateway-details/refresh`,
                GatewayTransactionDetailsFetchResultSchema,
                {
                    method: 'POST',
                }
            ),

        getReceiptData: (paymentTransactionUuid: string) =>
            fetchPillarAPI<GetReceiptDataResponse>(
                `/portal/v1/payments/vault/receipt-data/${paymentTransactionUuid}`,
                GetReceiptDataResponseSchema
            ),

        getReceiptHTML: (paymentTransactionUuid: string) =>
            requestTextWithSchema(
                (() => {
                    const endpoint = `/portal/v1/payments/vault/receipt-html/${paymentTransactionUuid}`;
                    // Intentionally verbose for live debugging of receipt-generation path.
                    console.info('[paymentsApi.getReceiptHTML] request:start', {
                        paymentTransactionUuid,
                        endpoint,
                    });
                    return endpoint;
                })(),
                z.string().min(1)
            ).then((html: string) => {
                console.info('[paymentsApi.getReceiptHTML] request:success', {
                    paymentTransactionUuid,
                    html_length: html.length,
                });
                return html;
            }).catch((error: unknown) => {
                console.error('[paymentsApi.getReceiptHTML] request:failure', {
                    paymentTransactionUuid,
                    error,
                });
                throw error;
            }),

        getReceiptHTMLUrl: (paymentTransactionUuid: string): string => {
            const endpoint = `/portal/v1/payments/vault/receipt-html/${paymentTransactionUuid}`;
            const normalizedEndpoint: string = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
            const url: string = baseUrl.endsWith('/')
                ? `${baseUrl.slice(0, -1)}${normalizedEndpoint}`
                : `${baseUrl}${normalizedEndpoint}`;
            console.info('[paymentsApi.getReceiptHTMLUrl] built', {
                paymentTransactionUuid,
                endpoint,
                url,
            });
            return url;
        },

        refundTransaction: dedupe((data: RefundTransactionRequest) =>
            fetchPillarAPI<RefundTransactionResponse>(
                '/portal/v1/payments/refund',
                RefundTransactionResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        voidTransaction: dedupe((data: VoidTransactionRequest) =>
            fetchPillarAPI<VoidTransactionResponse>(
                '/portal/v1/payments/void',
                VoidTransactionResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        terminalSale: dedupe((data: TerminalSaleRequest) =>
            fetchPillarAPI<TerminalSaleResponse>(
                '/portal/v1/payments/terminal/sale',
                TerminalSaleResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        terminalAbort: dedupe((data: TerminalAbortRequest) =>
            fetchPillarAPI<TerminalAbortResponse>(
                '/portal/v1/payments/terminal/abort',
                TerminalAbortResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordToken: dedupe((data: RecordTokenRequest) =>
            fetchPillarAPI<RecordTokenResponse>(
                '/portal/v1/payments/method/card-token',
                RecordTokenResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        processCardTokenPayment: dedupe((data: ProcessCardTokenPaymentRequest) =>
            fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordReconciliation: dedupe((data: RecordReconciliationRequest) =>
            fetchPillarAPI<RecordReconciliationResponse>(
                '/portal/v1/payments/reconcile',
                RecordReconciliationResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordPaymentRequestReconciliation: dedupe(
            (data: RecordPaymentRequestReconciliationRequest) =>
                fetchPillarAPI<RecordPaymentRequestReconciliationResponse>(
                    '/portal/v1/payments/payment-requests/reconcile',
                    RecordPaymentRequestReconciliationResponseSchema,
                    {
                        method: 'POST',
                        body: RecordPaymentRequestReconciliationRequestSchema.parse(data),
                    }
                )
        ),

        processAchPayment: dedupe((data: ProcessAchPaymentRequest) =>
            fetchPillarAPI<ProcessAchPaymentResponse>(
                '/portal/v1/payments/ach',
                ProcessAchPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        listCustomerPaymentMethods: (
            params: CustomerPaymentMethodListRequest = {}
        ) => {
            const validatedParams: CustomerPaymentMethodListRequest =
                CustomerPaymentMethodListRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-methods?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-methods';
            return fetchPillarAPI<CustomerPaymentMethodListResponse>(
                endpoint,
                CustomerPaymentMethodListResponseSchema
            );
        },

        getCustomerPaymentMethodBootstrap: (
            params: CustomerPaymentMethodBootstrapRequest = {}
        ) => {
            const validatedParams: CustomerPaymentMethodBootstrapRequest =
                CustomerPaymentMethodBootstrapRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-methods/add/bootstrap?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-methods/add/bootstrap';
            return fetchPillarAPI<CustomerPaymentMethodBootstrapResponse>(
                endpoint,
                CustomerPaymentMethodBootstrapResponseSchema
            );
        },

        customerPaymentMethodBinLookup: (data: CustomerPaymentMethodBinLookupRequest) =>
            fetchPillarAPI<BinLookupResponse>(
                '/portal/v1/payments/customer/payment-methods/add/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: CustomerPaymentMethodBinLookupRequestSchema.parse(data),
                }
            ),

        createCustomerPaymentMethod: (data: CreateCustomerPaymentMethodRequest) =>
            fetchPillarAPI<CreateCustomerPaymentMethodResponse>(
                '/portal/v1/payments/customer/payment-methods',
                CreateCustomerPaymentMethodResponseSchema,
                {
                    method: 'POST',
                    body: CreateCustomerPaymentMethodRequestSchema.parse(data),
                }
            ),

        updateCustomerPaymentMethod: dedupe((data: UpdateCustomerPaymentMethodRequest) =>
            fetchPillarAPI<UpdateCustomerPaymentMethodResponse>(
                `/portal/v1/payments/customer/payment-methods/${data.payment_method_token_uuid}`,
                UpdateCustomerPaymentMethodResponseSchema,
                {
                    method: 'PATCH',
                    body: UpdateCustomerPaymentMethodRequestSchema.parse(data),
                }
            )
        ),

        getCustomerPortalDashboard: (params: CustomerPortalDashboardRequest = {}) => {
            const validatedParams: CustomerPortalDashboardRequest =
                CustomerPortalDashboardRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/dashboard?${querySuffix}`
                    : '/portal/v1/payments/customer/dashboard';
            return fetchPillarAPI<CustomerPortalDashboardResponse>(
                endpoint,
                CustomerPortalDashboardResponseSchema
            );
        },

        listCustomerPaymentRequests: (params: CustomerPaymentRequestSearchRequest = {}) => {
            const requestBody: CustomerPaymentRequestSearchRequest =
                CustomerPaymentRequestSearchRequestSchema.parse(params);
            return fetchPillarAPI<CustomerPaymentRequestListResponse>(
                '/portal/v1/payments/customer/payment-requests/search',
                CustomerPaymentRequestListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getCustomerPaymentRequestDetails: (
            paymentRequestUuid: string,
            params: CustomerPaymentRequestDetailsRequest = {}
        ) => {
            const validatedParams: CustomerPaymentRequestDetailsRequest =
                CustomerPaymentRequestDetailsRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-requests/${paymentRequestUuid}?${querySuffix}`
                    : `/portal/v1/payments/customer/payment-requests/${paymentRequestUuid}`;
            return fetchPillarAPI<PaymentRequestDetailsView>(
                endpoint,
                PaymentRequestDetailsViewSchema
            );
        },

        listCustomerPaymentHistory: (params: CustomerPaymentHistoryRequest = {}) => {
            const validatedParams: CustomerPaymentHistoryRequest =
                CustomerPaymentHistoryRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-history?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-history';
            return fetchPillarAPI<CustomerPaymentHistoryResponse>(
                endpoint,
                CustomerPaymentHistoryResponseSchema
            );
        },
    };

    const sambaApi = {
        getVersion: (headers?: HttpRequestHeaders) =>
            fetchPillarAPI<SambaVersionResponse>('/samba/v1/version', SambaVersionResponseSchema, {
                headers,
            }),
        getLatestRelease: (params: SambaLatestReleaseQuery, headers?: HttpRequestHeaders) => {
            const queryParams: URLSearchParams = new URLSearchParams({
                platform: params.platform,
                arch: params.arch,
            });
            return fetchPillarAPI<SambaLatestReleaseResponse>(
                `/samba/latest?${queryParams.toString()}`,
                SambaLatestReleaseResponseSchema,
                {
                    headers,
                }
            );
        },
    };

    const diagnosticsApi = {
        getEnvironmentVariables: () =>
            fetchPillarAPI<Record<string, string>>(
                '/portal/v1/diagnostics/superadmin/env',
                EnvironmentVariablesResponseSchema
            ),

        getAwsCredentials: () =>
            fetchPillarAPI<AwsCredentialsResponse>(
                '/portal/v1/diagnostics/superadmin/aws-credentials',
                AwsCredentialsResponseSchema
            ),

        getCallerIdentity: () =>
            fetchPillarAPI<GetCallerIdentityResponse>(
                '/portal/v1/diagnostics/superadmin/aws-caller-identity',
                GetCallerIdentityResponseSchema
            ),

        getSystemInfo: () =>
            fetchPillarAPI<GetSystemInfoResponse>(
                '/portal/v1/diagnostics/superadmin/system-info',
                GetSystemInfoResponseSchema
            ),

        getSesDiagnostics: () =>
            fetchPillarAPI<GetSesDiagnosticsResponse>(
                '/portal/v1/diagnostics/superadmin/ses',
                GetSesDiagnosticsResponseSchema
            ),
    };

    const secretsApi = {
        listSecrets: (
            organization: SecretOrganizationReference | SecretOrganizationUuidReference,
            query?: ListSecretsForOrganizationQuery
        ) => {
            const parsedQuery: ListSecretsForOrganizationQuery | undefined =
                query !== undefined ? ListSecretsForOrganizationQuerySchema.parse(query) : undefined;
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.actions !== undefined) {
                for (const action of parsedQuery.actions) {
                    searchParams.append('actions', action);
                }
            }
            if (parsedQuery?.credential_type !== undefined) {
                searchParams.set('credential_type', parsedQuery.credential_type);
            }
            if (parsedQuery?.terms !== undefined) {
                for (const term of parsedQuery.terms) {
                    searchParams.append('terms', term);
                }
            }
            const organizationReferenceAttempt = SecretOrganizationReferenceSchema.safeParse(organization);
            const organizationUuidReferenceAttempt =
                SecretOrganizationUuidReferenceSchema.safeParse(organization);
            const organizationUuid: string = organizationReferenceAttempt.success
                ? organizationReferenceAttempt.data.organization_uuid
                : organizationUuidReferenceAttempt.success
                  ? organizationUuidReferenceAttempt.data.organization_uuid
                  : z.string().uuid().parse(organization);
            const serializedQuery: string = searchParams.toString();
            const endpoint: string =
                serializedQuery.length > 0
                    ? `/portal/v1/secrets/organization/${organizationUuid}?${serializedQuery}`
                    : `/portal/v1/secrets/organization/${organizationUuid}`;
            return fetchPillarAPI<ListSecretsResponse>(endpoint, ListSecretsResponseSchema);
        },

        getSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<GetSecretResponse>(
                `/portal/v1/secrets/${
                    (() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()
                }`,
                GetSecretResponseSchema
            ),

        getSecretWithCredentials: (organizationUuid: string, systemId: number, accountId: string) =>
            fetchPillarAPI<SecretWithCredentialsResponse>(
                `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}/credentials`,
                SecretWithCredentialsResponseSchema
            ),

        getSecretForUse: (organizationUuid: string, systemId: number, accountId: string) =>
            fetchPillarAPI<SecretWithCredentialsResponse>(
                `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}/use-credentials`,
                SecretWithCredentialsResponseSchema
            ),

        createSecret: dedupe((data: CreateSecretRequest) =>
            fetchPillarAPI<CreateSecretResponse>(
                '/portal/v1/secrets',
                CreateSecretResponseSchema,
                {
                    method: 'POST',
                    body: CreateSecretRequestSchema.parse(data),
                }
            )
        ),

        updateSecretByAccount: dedupe(
            (
                organizationUuid: string,
                systemId: number,
                accountId: string,
                data: UpdateSecretRequest
            ) =>
                fetchPillarAPI<SecretMetadataResponse>(
                    `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}`,
                    SecretMetadataResponseSchema,
                    {
                        method: 'PUT',
                        body: data,
                    }
                )
        ),

        updateSecret: dedupe((
            secret: SecretIdentityReference | SecretUuidReference,
            data: UpdateSecretRequest
        ) =>
            fetchPillarAPI<SecretMetadataResponse>(
                `/portal/v1/secrets/${
                    (() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()
                }`,
                SecretMetadataResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            )
        ),

        disableSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/secrets/${
                    (() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()
                }/disable`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        enableSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/secrets/${
                    (() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()
                }/enable`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        deleteSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/secrets/${
                    (() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()
                }`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        listCredentialUsages: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<CredentialUsagesResponse>(
                `/portal/v1/secrets/${
                    (() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()
                }/usages`,
                CredentialUsagesResponseSchema
            ),

        disconnectCredentialUsages: (
            secret: SecretIdentityReference | SecretUuidReference,
            data: DisconnectCredentialUsagesRequest
        ) =>
            fetchPillarAPI<DisconnectCredentialUsagesResponse>(
                `/portal/v1/secrets/${
                    (() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()
                }/disconnect`,
                DisconnectCredentialUsagesResponseSchema,
                {
                    method: 'POST',
                    body: DisconnectCredentialUsagesRequestSchema.parse(data),
                }
            ),
    };

    const permissionsApi = {
        getRoleAssignmentPermissions: (roleAssignmentUuid: string) =>
            fetchPillarAPI<RoleAssignmentPermissionsView>(
                `/portal/v1/permissions/role-assignment/${roleAssignmentUuid}`,
                RoleAssignmentPermissionsViewSchema
            ),

        auditRoleAssignmentPermissions: (roleAssignmentUuid: string) =>
            fetchPillarAPI<RoleAssignmentPermissionAuditResponse>(
                `/portal/v1/permissions/audit/role-assignment/${roleAssignmentUuid}`,
                RoleAssignmentPermissionAuditResponseSchema
            ),

        auditOrganizationPermissions: (organizationUuid: string) =>
            fetchPillarAPI<OrganizationPermissionAuditResponse>(
                `/portal/v1/permissions/audit/organization/${organizationUuid}`,
                OrganizationPermissionAuditResponseSchema
            ),

        grantPermission: (data: GrantPermissionRequest) =>
            fetchPillarAPI<void>('/portal/v1/permissions/grant', VoidResponseSchema, {
                method: 'POST',
                body: GrantPermissionRequestSchema.parse(data),
            }),

        revokePermission: (data: RevokePermissionRequest) =>
            fetchPillarAPI<void>('/portal/v1/permissions/revoke', VoidResponseSchema, {
                method: 'POST',
                body: RevokePermissionRequestSchema.parse(data),
            }),

        repairAuditedPermission: (data: RepairAuditedPermissionRequest) =>
            fetchPillarAPI<void>(
                '/portal/v1/permissions/audit/repair',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: RepairAuditedPermissionRequestSchema.parse(data),
                }
            ),

        listSharedBundles: () =>
            fetchPillarAPI<ListPublicBundlesResponse>(
                '/portal/v1/permissions/bundles/shared',
                ListPublicBundlesResponseSchema
            ),

        searchSharedBundles: (
            params: SharedPermissionBundlesSearchRequest = {}
        ) => {
            const requestBody: SharedPermissionBundlesSearchRequest =
                SharedPermissionBundlesSearchRequestSchema.parse(params);
            return fetchPillarAPI<SharedPermissionBundlesSearchPageResponse>(
                '/portal/v1/permissions/bundles/shared/search',
                SharedPermissionBundlesSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getBundle: (bundleUuid: string) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                PermissionsBundlePublicSchema
            ),

        createSharedBundle: (data: CreateSharedBundleRequest) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                '/portal/v1/permissions/bundles/shared',
                PermissionsBundlePublicSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updateBundle: (bundleUuid: string, data: UpdateBundleRequest) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        deleteBundle: (bundleUuid: string) =>
            fetchPillarAPI<void>(`/portal/v1/permissions/bundles/${bundleUuid}`, VoidResponseSchema, {
                method: 'DELETE',
            }),

        assignBundle: (data: AssignBundleRequest) =>
            fetchPillarAPI<void>('/portal/v1/permissions/assign-bundle', VoidResponseSchema, {
                method: 'POST',
                body: AssignBundleRequestSchema.parse(data),
            }),

        searchPolicyPermissionBundlesForOrganization: (
            organizationUuid: string,
            params: PolicyPermissionBundlesSearchRequest = {}
        ) => {
            const requestBody: PolicyPermissionBundlesSearchRequest =
                PolicyPermissionBundlesSearchRequestSchema.parse(params);
            return fetchPillarAPI<PolicyPermissionBundlesSearchPageResponse>(
                `/portal/v1/permissions/policy-bundles/organization/${organizationUuid}/search`,
                PolicyPermissionBundlesSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getPolicyPermissionBundle: (permissionsBundleUuid: string) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                PermissionsBundlePublicSchema
            ),

        createPolicyPermissionBundleForOrganization: (
            organizationUuid: string,
            data: CreatePolicyPermissionBundleRequest
        ) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/organization/${organizationUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updatePolicyPermissionBundle: (permissionsBundleUuid: string, data: UpdatePolicyPermissionBundleRequest) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        deletePolicyPermissionBundle: (permissionsBundleUuid: string) =>
            fetchPillarAPI<void>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        listPolicyPermissionGrantsForRoleAssignment: (roleAssignmentUuid: string) =>
            fetchPillarAPI<ListPolicyPermissionGrantsResponse>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}`,
                ListPolicyPermissionGrantsResponseSchema
            ),

        grantPolicyPermissionBundleToRoleAssignment: (
            roleAssignmentUuid: string,
            data: GrantPolicyPermissionBundleRequest
        ) => {
            const parsedData: GrantPolicyPermissionBundleRequest =
                GrantPolicyPermissionBundleRequestSchema.parse(data);
            return fetchPillarAPI<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parsedData,
                }
            );
        },

        revokePolicyPermissionBundleFromRoleAssignment: (
            roleAssignmentUuid: string,
            permissionsBundleUuid: string
        ) =>
            fetchPillarAPI<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}/${permissionsBundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        getOrganizationScopedPermissionSourceOptions: (organizationUuid: string) =>
            fetchPillarAPI<OrganizationScopedPermissionSourceOptionsResponse>(
                `/portal/v1/permissions/source-designators/organization/${organizationUuid}/options`,
                OrganizationScopedPermissionSourceOptionsResponseSchema
            ),

        searchRoleAssignmentsForOrganization: (
            organizationUuid: string,
            params: PermissionSourceRoleAssignmentsSearchRequest = {}
        ) => {
            const requestBody: PermissionSourceRoleAssignmentsSearchRequest =
                PermissionSourceRoleAssignmentsSearchRequestSchema.parse(params);
            return fetchPillarAPI<PermissionSourceRoleAssignmentsSearchPageResponse>(
                `/portal/v1/permissions/source-designators/organization/${organizationUuid}/role-assignments/search`,
                PermissionSourceRoleAssignmentsSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        searchRoleAssignmentsForPolicyPermissionBundle: (
            organizationUuid: string,
            permissionsBundleUuid: string,
            params: PolicyPermissionBundleRoleAssignmentsSearchRequest = {}
        ) => {
            const requestBody: PolicyPermissionBundleRoleAssignmentsSearchRequest =
                PolicyPermissionBundleRoleAssignmentsSearchRequestSchema.parse(params);
            return fetchPillarAPI<PolicyPermissionBundleRoleAssignmentsSearchPageResponse>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}/organization/${organizationUuid}/role-assignments/search`,
                PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getRoleEligibility: (role: string) =>
            fetchPillarAPI<RoleEligibility>(
                `/portal/v1/permissions/eligibility/${role}`,
                RoleEligibilitySchema
            ),
    };

    const authorizationApi = {
        canCreateUser: (organizationUuid: string) => {
            const validatedOrganizationUuid: string = z.string().uuid().parse(organizationUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                organization_uuid: validatedOrganizationUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_create_user?${queryParams.toString()}`,
                CanCreateUserResponseSchema
            );
        },

        canModifyUser: (userUuid: string) => {
            const validatedUserUuid: string = z.string().uuid().parse(userUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: validatedUserUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_modify_user?${queryParams.toString()}`,
                CanModifyUserResponseSchema
            );
        },

        canDeleteUser: (userUuid: string) => {
            const validatedUserUuid: string = z.string().uuid().parse(userUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: validatedUserUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_delete_user?${queryParams.toString()}`,
                CanDeleteUserResponseSchema
            );
        },

        canImpersonateRoleAssignment: (roleAssignmentUuid: string) => {
            const validatedRoleAssignmentUuid: string = z
                .string()
                .uuid()
                .parse(roleAssignmentUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                role_assignment_uuid: validatedRoleAssignmentUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_impersonate_role_assignment?${queryParams.toString()}`,
                CanImpersonateRoleAssignmentResponseSchema
            );
        },

        hasAccessToOrganization: (organizationUuid: string) => {
            const validatedOrganizationUuid: string = z
                .string()
                .uuid()
                .parse(organizationUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                organization_uuid: validatedOrganizationUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/has_access_to_organization?${queryParams.toString()}`,
                HasAccessToOrganizationResponseSchema
            );
        },

        hasAccessToUser: (userUuid: string) => {
            const validatedUserUuid: string = z.string().uuid().parse(userUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: validatedUserUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/has_access_to_user?${queryParams.toString()}`,
                HasAccessToUserResponseSchema
            );
        },

        getAccessibleOrganizationIds: (params?: AccessibleOrganizationIdsRequest) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_organization_ids?${queryString}`
                    : '/portal/v1/authorization/accessible_organization_ids';
            return fetchPillarAPI(url, AccessibleOrganizationIdsResponseSchema);
        },

        getAccessibleOrganizations: (params?: AccessibleOrganizationIdsRequest) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor.toString());
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_organizations?${queryString}`
                    : '/portal/v1/authorization/accessible_organizations';
            return fetchPillarAPI(url, AccessibleOrganizationsResponseSchema);
        },

        getAccessibleUserIds: (params?: AccessibleUserIdsRequestNoPkey) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.organization_uuid !== undefined) {
                const validatedOrganizationUuid: string = z
                    .string()
                    .uuid()
                    .parse(params.organization_uuid);
                queryParams.set('organization_uuid', validatedOrganizationUuid);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_user_ids?${queryString}`
                    : '/portal/v1/authorization/accessible_user_ids';
            return fetchPillarAPI(url, AccessibleUserIdsResponseSchema);
        },

        getAccessibleUsers: (params?: AccessibleUserIdsRequestNoPkey) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.organization_uuid !== undefined) {
                const validatedOrganizationUuid: string = z
                    .string()
                    .uuid()
                    .parse(params.organization_uuid);
                queryParams.set('organization_uuid', validatedOrganizationUuid);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_users?${queryString}`
                    : '/portal/v1/authorization/accessible_users';
            return fetchPillarAPI(url, AccessibleUsersResponseSchema);
        },

        isAuthorized: (params: IsAuthorizedRequest) => {
            return fetchPillarAPI('/portal/v1/authorization/is_authorized', IsAuthorizedResponseSchema, {
                method: 'POST',
                body: IsAuthorizedRequestSchema.parse(params),
            });
        },
    };

    const legalDocumentsApi = {
        searchLegalDocumentsPage: (params: LegalDocumentsSearchRequest = {}) => {
            const requestBody: LegalDocumentsSearchRequest =
                LegalDocumentsSearchRequestSchema.parse(params);
            return fetchPillarAPI<SearchLegalDocumentsPageResponse>(
                '/portal/v1/legal_documents/search',
                SearchLegalDocumentsPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        createLegalDocument: (data: CreateLegalDocumentRequest) => {
            const parseResult = CreateLegalDocumentRequestSchema.safeParse(data);
            if (!parseResult.success) {
                throw new Error(`Invalid create legal document request: ${parseResult.error.message}`);
            }
            return fetchPillarAPI<CreateLegalDocumentResponse>(
                '/portal/v1/legal_documents',
                CreateLegalDocumentResponseSchema,
                {
                    method: 'POST',
                    body: parseResult.data,
                }
            );
        },

        getLegalDocument: (legalDocumentUuid: string) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            return fetchPillarAPI<GetLegalDocumentResponse>(
                `/portal/v1/legal_documents/${idParseResult.data}`,
                GetLegalDocumentResponseSchema
            );
        },

        listPendingRequiredLegalDocuments: () =>
            fetchPillarAPI<ListPendingRequiredLegalDocumentsResponse>(
                '/portal/v1/legal_documents/pending',
                ListPendingRequiredLegalDocumentsResponseSchema
            ),

        acceptLegalDocument: (legalDocumentUuid: string, data: AcceptLegalDocumentRequest) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            const parseResult = AcceptLegalDocumentRequestSchema.safeParse(data);
            if (!parseResult.success) {
                throw new Error(`Invalid accept legal document request: ${parseResult.error.message}`);
            }
            return fetchPillarAPI<void>(
                `/portal/v1/legal_documents/${idParseResult.data}/accept`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parseResult.data,
                }
            );
        },
    };

    const auditApi = {
        listTransactionLogPage: (params: TransactionLogSearchRequest = {}) => {
            const requestBody: TransactionLogSearchRequest =
                TransactionLogSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListTransactionLogPageResponse>(
                '/portal/v1/audit/transaction_log/search',
                ListTransactionLogPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },
    };

    const appApi = {};

    return {
        baseUrl,
        requestWithSchema,
        authApi,
        userApi,
        organizationApi,
        customerApi,
        paymentsInfrastructureApi,
        sambaConfigApi,
        paymentsApi,
        sambaApi,
        diagnosticsApi,
        secretsApi,
        permissionsApi,
        authorizationApi,
        legalDocumentsApi,
        auditApi,
        appApi,
    };
}

export type PillarApiClient = ReturnType<typeof createPillarApiWithOptions>;
